/* ==========================================================================
   Taton — global chrome behaviour

   Four independent pieces, each a no-op when its markup is absent:
     1. Scroll reveal      — IntersectionObserver, once per element
     2. Header             — transparent over a hero, cream past 24px
     3. Side menu + search — off-canvas drawer and the search sheet
     4. Theme toggle       — light / dark, remembered

   Progressive enhancement: without JS the header renders opaque, the menu is
   a plain link list, and reveals are visible (see [data-reveal] in taton.css,
   which this file switches off at boot).
   ========================================================================== */

// Words the script writes itself, handed over by layout/theme.liquid from the
// locale files (taton.js.*), so a second storefront language needs no JS edit.
// Every use keeps its English fallback — 16 September 2026.
const TI = (typeof window !== 'undefined' && window.tatonI18n) || {};

(function () {
  'use strict';

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* Гроші так, як їх друкує Liquid `money_without_trailing_zeros` у форматі
     магазину — 29 вересня 2026, перед переходом taton.shop на «1 645 грн».

     Було дві копії (товар після вибору варіанта і сума під «Збереженим»),
     і обидві знали лише кому між тисячами: з форматом
     `amount_no_decimals_with_space_separator` вони надрукували б
     «1,645 грн» поруч із «1 645 грн» від Liquid. Тепер одна функція і всі
     заповнювачі Shopify — той самий список, що в Horizon
     `money-formatting.js`.

     Два свідомі відступи від Liquid: пробіл між тисячами нерозривний, щоб
     «1 645» не розпадалось між рядками; розмітку й сутності формату
     (`<span class=money>`, `&nbsp;`) прибрано — результат завжди йде як
     текст. */
  function tatonMoney(cents, format) {
    const whole = cents % 100 === 0;
    const text = String(format || '{{amount}}')
      .replace(/<[^>]*>/g, '')
      .replace(/\{\{\s*(\w+)\s*\}\}/g, (_, token) => {
        if (token === 'currency') {
          return (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || '';
        }
        let thousands = ',';
        let decimal = '.';
        let precision = whole ? 0 : 2;
        switch (token) {
          case 'amount_no_decimals':
            precision = 0;
            break;
          case 'amount_with_comma_separator':
            thousands = '.';
            decimal = ',';
            break;
          case 'amount_no_decimals_with_comma_separator':
            thousands = '.';
            precision = 0;
            break;
          case 'amount_no_decimals_with_space_separator':
            thousands = ' ';
            precision = 0;
            break;
          case 'amount_with_space_separator':
            thousands = ' ';
            decimal = ',';
            break;
          case 'amount_with_period_and_space_separator':
            thousands = ' ';
            break;
          case 'amount_with_apostrophe_separator':
            thousands = "'";
            break;
          default:
            break;
        }
        const [int, frac] = (cents / 100).toFixed(precision).split('.');
        const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
        return frac ? grouped + decimal + frac : grouped;
      });
    const decode = document.createElement('textarea');
    decode.innerHTML = text;
    return decode.value;
  }

  /* ------------------------------------------------------------------
     1. Scroll reveal
     ------------------------------------------------------------------ */

  function initReveal() {
    const targets = document.querySelectorAll('[data-reveal]:not([data-reveal-bound])');
    if (!targets.length) return;

    // Reduced motion: show everything, never animate.
    if (reduceMotion.matches || !('IntersectionObserver' in window)) {
      targets.forEach((el) => {
        el.setAttribute('data-reveal-bound', '');
        el.style.opacity = '1';
      });
      return;
    }

    const observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.setAttribute('data-reveal-in', '');
          obs.unobserve(entry.target);
        });
      },
      { rootMargin: '-6% 0px -10% 0px' }
    );

    targets.forEach((el) => {
      el.setAttribute('data-reveal-bound', '');
      observer.observe(el);
    });
  }

  /* ------------------------------------------------------------------
     2. Header — transparent over a hero, cream past 24px

     The threshold is deliberately small: the header settles almost as soon
     as the page moves, rather than waiting for the hero to clear.
     ------------------------------------------------------------------ */

  const SCROLL_THRESHOLD = 24;

  function initHeader() {
    const chrome = document.querySelector('[data-taton-chrome]');
    if (!chrome) return;

    // Publish the chrome height so templates can offset their content.
    // --header-height / --header-group-height keep Horizon's own components
    // (drawers, sticky bars) positioned correctly against our header.
    const publishHeight = () => {
      const h = chrome.offsetHeight;
      const root = document.documentElement.style;
      root.setProperty('--taton-chrome-h', h + 'px');
      root.setProperty('--header-height', h + 'px');
      root.setProperty('--header-group-height', h + 'px');
    };
    publishHeight();
    if ('ResizeObserver' in window) new ResizeObserver(publishHeight).observe(chrome);

    if (!chrome.hasAttribute('data-transparent')) return;

    // Horizon scrolls an inner container, not the window: html and body are
    // pinned to the viewport with overflow:hidden and .page-wrapper does the
    // scrolling. Watch both, so this keeps working if that ever changes.
    const wrapper = document.querySelector('.page-wrapper');
    const offset = () => Math.max(window.scrollY || 0, wrapper ? wrapper.scrollTop : 0);

    let scrolled = null; // null forces the first write
    const sync = () => {
      const next = offset() > SCROLL_THRESHOLD;
      if (next === scrolled) return;
      scrolled = next;
      chrome.toggleAttribute('data-scrolled', next);
    };

    sync();
    window.addEventListener('scroll', sync, { passive: true });
    if (wrapper) wrapper.addEventListener('scroll', sync, { passive: true });
  }

  /* ------------------------------------------------------------------
     3. Side menu, search sheet

     Both live behind the same scrim rules: Escape closes, the scrim closes,
     and body scroll locks while the drawer is open.
     ------------------------------------------------------------------ */

  // Horizon locks scrolling with a `scroll-lock` attribute on <html>, which
  // its base.css applies to .page-wrapper as well. Setting overflow on <body>
  // would do nothing here, because <body> is not the scroll container.
  function lockScroll(locked) {
    document.documentElement.toggleAttribute('scroll-lock', locked);
  }

  function initMenu() {
    const menu = document.querySelector('[data-taton-menu]');
    const scrim = document.querySelector('[data-taton-scrim]');
    if (!menu) return;

    const openers = document.querySelectorAll('[data-taton-menu-open]');
    const closers = document.querySelectorAll('[data-taton-menu-close]');

    const setOpen = (open) => {
      menu.toggleAttribute('data-open', open);
      if (scrim) scrim.toggleAttribute('data-open', open);
      openers.forEach((b) => b.setAttribute('aria-expanded', String(open)));
      menu.setAttribute('aria-hidden', String(!open));
      /* inert, not only aria-hidden: a closed menu kept its links in the tab
         order, so the keyboard walked into a panel nobody could see
         (Lighthouse aria-hidden-focus, 30 Sept 2026). */
      menu.inert = !open;
      lockScroll(open);
      if (open) {
        const first = menu.querySelector('a, button');
        if (first) first.focus({ preventScroll: true });
      }
    };

    setOpen(false);
    openers.forEach((b) => b.addEventListener('click', () => setOpen(true)));
    closers.forEach((b) => b.addEventListener('click', () => setOpen(false)));
    if (scrim) scrim.addEventListener('click', () => setOpen(false));

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && menu.hasAttribute('data-open')) setOpen(false);
    });

    // A parent with children expands in place rather than navigating.
    menu.querySelectorAll('[data-taton-submenu-toggle]').forEach((toggle) => {
      const panel = document.getElementById(toggle.getAttribute('aria-controls'));
      if (!panel) return;

      toggle.addEventListener('click', (e) => {
        e.preventDefault();
        const open = toggle.getAttribute('aria-expanded') === 'true';
        toggle.setAttribute('aria-expanded', String(!open));
        panel.style.height = open ? '0px' : panel.scrollHeight + 'px';
      });
    });
  }

  function initSearch() {
    const sheet = document.querySelector('[data-taton-search]');
    if (!sheet) return;

    const toggles = document.querySelectorAll('[data-taton-search-toggle]');
    const input = sheet.querySelector('input[type="search"], input[name="q"]');

    const setOpen = (open) => {
      sheet.toggleAttribute('data-open', open);
      sheet.setAttribute('aria-hidden', String(!open));
      sheet.inert = !open;
      toggles.forEach((b) => b.setAttribute('aria-expanded', String(open)));
      if (open && input) input.focus({ preventScroll: true });
    };

    setOpen(false);
    toggles.forEach((b) =>
      b.addEventListener('click', () => setOpen(!sheet.hasAttribute('data-open')))
    );
    sheet.querySelectorAll('[data-taton-search-close]').forEach((b) =>
      b.addEventListener('click', () => setOpen(false))
    );

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && sheet.hasAttribute('data-open')) setOpen(false);
    });
  }

  /* ------------------------------------------------------------------
     4. Theme toggle

     The stored preference is applied before first paint by an inline script
     in layout/theme.liquid; this only handles the switch itself.
     ------------------------------------------------------------------ */

  function initTheme() {
    const toggles = document.querySelectorAll('[data-taton-theme-toggle]');
    if (!toggles.length) return;

    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)');

    const current = () => {
      const set = document.documentElement.getAttribute('data-theme');
      if (set === 'dark' || set === 'light') return set;
      return prefersDark.matches ? 'dark' : 'light';
    };

    const label = () => {
      const next = current() === 'dark' ? 'light' : 'dark';
      toggles.forEach((b) => {
        b.textContent = next === 'dark' ? (TI.dark || 'Dark') : (TI.light || 'Light');
        b.setAttribute('aria-label', (TI.switch_to || 'Switch to ') + next + ' theme');
      });
    };

    label();

    toggles.forEach((b) =>
      b.addEventListener('click', () => {
        const next = current() === 'dark' ? 'light' : 'dark';
        document.documentElement.setAttribute('data-theme', next);
        // Session, not local: the choice lasts the visit and the next one
        // starts from the phone again. See the note in layout/theme.liquid.
        try {
          sessionStorage.setItem('taton-theme', next);
        } catch (e) {}
        label();
      })
    );

    // Follow the OS while the customer has not chosen for themselves.
    prefersDark.addEventListener('change', () => {
      if (!document.documentElement.hasAttribute('data-theme')) label();
    });
  }

  /* ------------------------------------------------------------------
     5. Announcement bar — one message at a time, cross-fading
     ------------------------------------------------------------------ */

  function initAnnouncements() {
    const bar = document.querySelector('[data-taton-announcements]');
    if (!bar) return;

    if (bar.querySelectorAll('[data-taton-announcement]').length < 2) return;

    // Read on every turn rather than once: the Kept message (module 14)
    // is hidden until something is kept, and it can appear or go while the
    // page is open — a heart pressed, a list emptied on the Kept page.
    const visible = () => Array.from(bar.querySelectorAll('[data-taton-announcement]:not([hidden])'));

    const speed = (parseInt(bar.getAttribute('data-speed'), 10) || 5) * 1000;
    const kept = bar.querySelector('[data-taton-promo-kept]');
    const hold = (parseInt(bar.getAttribute('data-kept-hold'), 10) || 0) * 1000;
    const only = bar.hasAttribute('data-kept-only');
    let holdUntil = 0;

    const show = (msg) => {
      bar.querySelectorAll('[data-taton-announcement][data-current]').forEach((m) => m.removeAttribute('data-current'));
      msg.setAttribute('data-current', '');
    };

    /* The Kept message, two ways of being more than one more announcement —
       Anton, 8 September 2026, Theme settings → Kept:
         hold   a heart was just pressed: the band answers at once and keeps
                the answer for `hold` before the rotation goes on;
         only   his first sketch — once something is kept, this is the only
                message until the list is empty again.
       Module 14 fires the event whenever the count is synced, with whether
       it just went up. Not motion, so reduced-motion does not stop it. */
    document.addEventListener('taton:kept-promo', (e) => {
      if (!kept || kept.hidden) return;
      const d = (e && e.detail) || {};
      if (only) {
        show(kept);
      } else if (d.increased) {
        show(kept);
        holdUntil = Date.now() + hold;
      }
    });

    // Reduced motion: hold the first message rather than cycling.
    if (reduceMotion.matches) return;

    setInterval(() => {
      if (Date.now() < holdUntil) return;
      if (only && kept && !kept.hidden) {
        if (!kept.hasAttribute('data-current')) show(kept);
        return;
      }
      const messages = visible();
      if (messages.length < 2) return;
      const current = messages.findIndex((m) => m.hasAttribute('data-current'));
      // Clears a message that went hidden while it was the current one too.
      show(messages[(current + 1) % messages.length]);
    }, speed);
  }

  /* ------------------------------------------------------------------
     6. Product cards — photo swipe, dots, heart, quick add

     The card is a link with controls layered on top, so every control has
     to stop propagation or it navigates instead of acting. A swipe must not
     fire the link either.
     ------------------------------------------------------------------ */

  const SWIPE_THRESHOLD = 40;

  /* ------------------------------------------------------------------
     Tap the edge of a photograph to step through the set.

     Anton, 18 August 2026: swiping was the only way to see the second
     photograph, and on a desktop there is no swipe at all — the dots were
     the whole interface and they are 6px across.

     Two transparent buttons, injected rather than written into each
     template, so the product card and the product page cannot drift apart.
     Buttons rather than click coordinates on the container: they stop the
     event reaching the link underneath by themselves, and they arrive
     keyboard-operable and labelled for free.
     ------------------------------------------------------------------ */

  function addPhotoZones(container, step) {
    if (!container || container.querySelector('.taton-photo-zone')) return;

    const make = (dir, label) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'taton-photo-zone taton-photo-zone--' + (dir < 0 ? 'prev' : 'next');
      b.setAttribute('aria-label', label);
      b.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        step(dir);
      });
      return b;
    };

    container.appendChild(make(-1, TI.previous_photo || 'Previous photo'));
    container.appendChild(make(1, TI.next_photo || 'Next photo'));
  }

  function initCard(card) {
    card.setAttribute('data-taton-card-bound', '');

    const media = card.querySelector('[data-taton-card-media]');
    const images = Array.from(card.querySelectorAll('[data-taton-card-image]'));
    const dots = Array.from(card.querySelectorAll('[data-taton-card-dot]'));

    let index = 0;

    const show = (next) => {
      if (!images.length) return;
      index = (next + images.length) % images.length;
      images.forEach((img, n) => img.setAttribute('data-current', String(n === index)));
      dots.forEach((d, n) => d.setAttribute('aria-selected', String(n === index)));
    };

    dots.forEach((dot, n) =>
      dot.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        show(n);
      })
    );

    // Inside a rail the horizontal gesture belongs to the rail: the customer
    // is browsing products, not photos. Dots still change the photo.
    const inRail = Boolean(card.closest('[data-taton-rail]'));

    // The edge zones work everywhere, rail included — a tap is not a drag, so
    // it takes nothing away from the rail's own gesture.
    if (media && images.length > 1) addPhotoZones(media, (dir) => show(index + dir));

    if (media && images.length > 1 && !inRail) {
      let startX = null;
      let startY = null;
      let swiped = false;

      media.addEventListener(
        'touchstart',
        (e) => {
          startX = e.touches[0].clientX;
          startY = e.touches[0].clientY;
          swiped = false;
        },
        { passive: true }
      );

      media.addEventListener(
        'touchmove',
        (e) => {
          if (startX === null) return;
          const dx = e.touches[0].clientX - startX;
          const dy = e.touches[0].clientY - startY;
          // Horizontal only — a mostly-vertical drag is the page scrolling.
          if (Math.abs(dx) > SWIPE_THRESHOLD && Math.abs(dx) > Math.abs(dy) * 1.4) {
            show(index + (dx < 0 ? 1 : -1));
            startX = null;
            swiped = true;
          }
        },
        { passive: true }
      );

      media.addEventListener('touchend', () => {
        startX = null;
      });

      // Capture phase: swallow the click a swipe would otherwise produce.
      media.addEventListener(
        'click',
        (e) => {
          if (!swiped) return;
          e.preventDefault();
          e.stopPropagation();
          swiped = false;
        },
        true
      );
    }

    /* The heart writes to the Saved store (module 14, docs/saved.md) and
       every heart on the page follows. Until 7 September 2026 it flipped its
       own glyph and a reload forgot it — three buttons promising to save and
       saving nothing. */
    const heart = card.querySelector('[data-taton-card-heart]');
    if (heart && card.getAttribute('data-taton-card-handle')) {
      savedBindHeart(heart, () => savedEntryFromCard(card));
    }

    /* --- Finish swatches ---
       Pressing one swaps the photograph, re-points every link and quick-add
       at that variant, and updates the price. Before this the card could only
       ever add whichever variant Shopify listed first, so half of a
       two-finish product was unreachable from anywhere but its own page. */
    const swatches = Array.from(card.querySelectorAll('[data-taton-card-swatch]'));
    if (swatches.length > 1) {
      // `.taton-card__action-link` is the Choose / Notify button, which is an
      // <a>: pressing a swatch and then it must land on that finish's page,
      // not on whichever variant Shopify listed first.
      const links = Array.from(
        card.querySelectorAll('.taton-card__link, .taton-card__title, .taton-card__action-link')
      );
      const priceEl = card.querySelector('.taton-card__price');
      const addBtn = card.querySelector('[data-taton-card-add]');

      swatches.forEach((sw) => {
        sw.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();

          const mediaId = sw.getAttribute('data-media-id');
          const frame = images.findIndex((img) => img.getAttribute('data-media-id') === mediaId);
          if (frame >= 0) show(frame);

          swatches.forEach((s) => s.setAttribute('aria-pressed', String(s === sw)));

          const url = sw.getAttribute('data-url');
          if (url) {
            links.forEach((a) => {
              // Keep any fragment the link already carried — "Notify me"
              // points at #waitlist, and dropping it lands the customer on
              // the page but not on the form it promised.
              const hash = (a.getAttribute('href') || '').split('#')[1];
              a.setAttribute('href', hash ? url + '#' + hash : url);
            });
          }

          const price = sw.getAttribute('data-price');
          if (price && priceEl) priceEl.textContent = price;

          if (addBtn) {
            addBtn.setAttribute('data-variant-id', sw.getAttribute('data-variant-id'));
            // A finish that is out of stock must not be addable — the button
            // would fail silently against Shopify and read as broken.
            addBtn.disabled = sw.getAttribute('data-available') === '0';
          }
        });
      });
    }

    const add = card.querySelector('[data-taton-card-add]');
    if (add) {
      add.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        const id = add.getAttribute('data-variant-id');
        if (!id || add.hasAttribute('data-adding')) return;

        add.setAttribute('data-adding', '');
        add.setAttribute('data-added', '');
        window.setTimeout(() => add.removeAttribute('data-added'), 1400);

        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

        // A card on a list a friend sent: the piece is a gift (6 Oct 2026).
        const gift = window.tatonGiftFor && card.closest('[data-taton-saved-grid]') ? window.tatonGiftFor : null;
        const item = { id: Number(id), quantity: 1 };
        if (gift) item.properties = { [gift.key]: gift.value };

        fetch(root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [item] }),
        })
          .then((r) => (r.ok ? fetch(root + 'cart.js') : Promise.reject(r)))
          .then((r) => r.json())
          .then((cart) => {
            document.querySelectorAll('[data-taton-cart-count]').forEach((el) => {
              el.textContent = cart.item_count;
            });
            // Horizon's own components listen for this.
            document.dispatchEvent(new CustomEvent('cart:update', { detail: { cart } }));

            /* **The piece was added and the bag did not say so — Anton, 18
               August 2026.**

               Only the counter was updated here. Press quick-add while the
               drawer is open — which is exactly when a customer reaches for
               it, because the drawer is where the "add one more" suggestions
               live — and the lines, the tier bar and the gift picker all kept
               showing the bag as it was a moment ago. The item really was in
               the cart; nothing on screen agreed. So it reads as a dead
               button, and the natural response is to press it again.

               Every other cart mutation in this theme re-renders the section
               from the server so the ladder is computed in one place. This one
               was the exception.

               **The direct call is gone — 20 August 2026.** It sat next to the
               `cart:update` dispatch above, whose listener also refreshes the
               drawer, so one press fetched the section twice and opened the
               panel twice. The event is the single route now; the cart page
               still needs its own redraw because the listener deliberately
               leaves that surface alone. */
            if (refreshCartPage) refreshCartPage();
          })
          .catch(() => {})
          .finally(() => add.removeAttribute('data-adding'));
      });
    }
  }

  function initCards() {
    document
      .querySelectorAll('[data-taton-card]:not([data-taton-card-bound])')
      .forEach(initCard);
  }

  /* ------------------------------------------------------------------
     7. Snap rails — round outline arrows

     Scrolls by one card width, so the snap points always line up.
     ------------------------------------------------------------------ */

  function initRails() {
    document.querySelectorAll('[data-taton-rail]:not([data-taton-rail-bound])').forEach((rail) => {
      rail.setAttribute('data-taton-rail-bound', '');

      /* **The cart's two rails were unreachable with a mouse — Anton, 20
         August 2026.** *"В корзині зараз немає як гортати товари з
         комп'ютера."* Drag-to-scroll worked, but nothing said so: no arrows,
         no scrollbar, and on a desktop there is no swipe to discover it by
         accident. The gift shelf and the recommendations now carry the same
         arrows as the product rails, so `.taton-gifts` and `.taton-recs` have
         to be findable as the rail's owning block. */
      const section =
        rail.closest('.taton-rail-section, .taton-strip, .taton-gifts, .taton-recs') || document;
      // Arrows are still optional — a rail without them keeps drag-to-scroll.
      const prev = section.querySelector('[data-taton-rail-prev]');
      const next = section.querySelector('[data-taton-rail-next]');
      // The home strip carries a progress line as well, because a peeking
      // tile says there is more without saying how much more.
      const bar = section.querySelector('[data-taton-rail-bar]');

      const step = () => {
        const item = rail.firstElementChild;
        if (!item) return rail.clientWidth;
        const gap = parseFloat(getComputedStyle(rail).columnGap) || 0;
        return item.getBoundingClientRect().width + gap;
      };

      const syncDisabled = () => {
        const max = rail.scrollWidth - rail.clientWidth - 1;
        if (prev) prev.disabled = rail.scrollLeft <= 0;
        if (next) next.disabled = rail.scrollLeft >= max;
        if (!bar) return;

        const shown = rail.clientWidth / rail.scrollWidth;
        if (shown >= 0.999) {
          // Nothing to scroll: the line would be a full bar claiming a
          // gesture that does not exist.
          if (section !== document) section.setAttribute('data-fits', '');
          return;
        }
        if (section !== document) section.removeAttribute('data-fits');

        const travelled = max > 0 ? Math.min(1, Math.max(0, rail.scrollLeft / max)) : 0;
        bar.style.width = shown * 100 + '%';
        bar.style.transform = 'translateX(' + travelled * (100 / shown - 100) + '%)';
      };

      if (prev) prev.addEventListener('click', () => rail.scrollBy({ left: -step() }));
      if (next) next.addEventListener('click', () => rail.scrollBy({ left: step() }));

      rail.addEventListener('scroll', syncDisabled, { passive: true });
      window.addEventListener('resize', syncDisabled);
      syncDisabled();

      /* **A rail that is measured while it has no size answers nonsense —
         added 9 September 2026, with `content-visibility: hidden` on the
         closed cart drawer.** Inside a skipped subtree `scrollWidth` and
         `clientWidth` are both 0, so `shown` is NaN, the progress line gets a
         width of `NaN%` and the forward arrow disables itself. The rail then
         stays wrong until something resizes the window, which on a phone is
         never. A ResizeObserver re-runs the sync on the frame the rail first
         has a width — the frame the drawer opens — and costs nothing on the
         rails that had a size all along, because it fires once for them too. */
      if (typeof ResizeObserver === 'function') {
        new ResizeObserver(syncDisabled).observe(rail);
      }

      /* Drag to scroll with a mouse. Touch already scrolls the rail
         natively, so this is gated to pointerType mouse — otherwise it
         would fight the card's own photo swipe on a phone. */
      let dragging = false;
      let startX = 0;
      let startLeft = 0;
      let moved = false;
      let snap = '';

      let captured = 0;

      rail.addEventListener('pointerdown', (e) => {
        if (e.pointerType !== 'mouse' || e.button !== 0) return;
        dragging = true;
        moved = false;
        startX = e.clientX;
        startLeft = rail.scrollLeft;
        snap = rail.style.scrollSnapType;
      });

      /* **Capture only once the pointer has actually moved.**

         This used to capture on pointerdown, and that silently killed every
         link inside every rail: while a pointer is captured the browser
         dispatches the click to the capturing element, so the `click` landed
         on the rail and the product card's anchor was never activated. The
         recommendations under a product, the recommendations in the cart and
         the strips on the home page are all rails — none of them opened —
         while the collection grid, which is not a rail, worked perfectly. That
         difference was the whole clue.

         Capture is still wanted, but only for a real drag, so it is taken at
         the same moment `moved` becomes true. A click never moves three
         pixels, so a click is never captured. */
      rail.addEventListener('pointermove', (e) => {
        if (!dragging) return;
        const dx = e.clientX - startX;
        if (Math.abs(dx) > 3 && !moved) {
          moved = true;
          rail.style.scrollSnapType = 'none';
          rail.setAttribute('data-dragging', '');
          try {
            rail.setPointerCapture(e.pointerId);
            captured = e.pointerId;
          } catch (err) {}
        }
        if (!moved) return;
        rail.scrollLeft = startLeft - dx;
      });

      const endDrag = () => {
        if (!dragging) return;
        dragging = false;
        if (captured) {
          try {
            rail.releasePointerCapture(captured);
          } catch (err) {}
          captured = 0;
        }
        rail.style.scrollSnapType = snap;
        rail.removeAttribute('data-dragging');
      };

      rail.addEventListener('pointerup', endDrag);
      rail.addEventListener('pointercancel', endDrag);

      // A drag must not open the product it finished on.
      rail.addEventListener(
        'click',
        (e) => {
          if (!moved) return;
          e.preventDefault();
          e.stopPropagation();
          moved = false;
        },
        true
      );
    });
  }

  /* ------------------------------------------------------------------
     8. Collection — sort, pinned sub-nav, load on scroll
     ------------------------------------------------------------------ */

  function initSort() {
    const select = document.querySelector('[data-taton-sort]:not([data-taton-sort-bound])');
    if (!select) return;
    select.setAttribute('data-taton-sort-bound', '');

    select.addEventListener('change', () => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', select.value);
      url.searchParams.delete('page');
      window.location.assign(url.toString());
    });
  }

  function chromeHeight() {
    const raw = getComputedStyle(document.documentElement).getPropertyValue('--taton-chrome-h');
    return parseFloat(raw) || 102;
  }

  function initSubnav() {
    const strip = document.querySelector('[data-taton-subnav-strip]');
    const bar = document.querySelector('[data-taton-subnav-bar]');
    if (!strip || !bar || !('IntersectionObserver' in window)) return;
    if (bar.hasAttribute('data-taton-subnav-bound')) return;
    bar.setAttribute('data-taton-subnav-bound', '');

    new IntersectionObserver(
      ([entry]) => {
        // Pin only once the strip has passed ABOVE the header, never when it
        // is simply further down the page than the viewport reaches.
        const scrolledPast = entry.boundingClientRect.top < 0;
        const pinned = !entry.isIntersecting && scrolledPast;
        bar.toggleAttribute('data-pinned', pinned);
        bar.setAttribute('aria-hidden', String(!pinned));
        bar.inert = !pinned;
      },
      { rootMargin: '-' + chromeHeight() + 'px 0px 0px 0px', threshold: 0 }
    ).observe(strip);
  }

  function initLoadMore() {
    const sentinel = document.querySelector('[data-taton-load-more]:not([data-taton-more-bound])');
    if (!sentinel || !('IntersectionObserver' in window)) return;
    sentinel.setAttribute('data-taton-more-bound', '');

    const grid = document.querySelector('[data-taton-grid]');
    if (!grid) return;

    const total = Number(sentinel.getAttribute('data-total')) || 0;
    let loading = false;

    // The link is for crawlers and for a browser without scripts; here the
    // next page loads in place, so a tap on it must not leave the page.
    const link = sentinel.querySelector('a');
    if (link) link.addEventListener('click', (e) => e.preventDefault());

    const finish = (observer) => {
      sentinel.removeAttribute('data-next-url');
      sentinel.textContent = TI.end || "That's everything";
      observer.disconnect();
    };

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting || loading) return;

        const url = sentinel.getAttribute('data-next-url');
        if (!url) return finish(observer);

        loading = true;

        fetch(url)
          .then((r) => (r.ok ? r.text() : Promise.reject(r)))
          .then((html) => {
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const items = doc.querySelectorAll('[data-taton-grid] > .taton-grid__item');
            items.forEach((item) => grid.appendChild(document.importNode(item, true)));

            const next = doc.querySelector('[data-taton-load-more]');
            const nextUrl = next && next.getAttribute('data-next-url');
            if (nextUrl) {
              sentinel.setAttribute('data-next-url', nextUrl);
              const link = sentinel.querySelector('a');
              if (link) link.href = nextUrl;
            } else {
              finish(observer);
            }

            const shown = grid.children.length;
            const shownEl = document.querySelector('[data-taton-shown]');
            if (shownEl) shownEl.textContent = shown;

            const fill = document.querySelector('[data-taton-progress]');
            if (fill && total) fill.style.width = Math.min(100, (shown / total) * 100) + '%';

            initCards();
          })
          .catch(() => {
            sentinel.textContent = TI.load_more_failed || 'Could not load more';
          })
          .finally(() => {
            loading = false;
          });
      },
      { rootMargin: '240px' }
    );

    observer.observe(sentinel);
  }

  /* ------------------------------------------------------------------
     9. Product page — gallery, variants

     Mobile and desktop render separate frames of the same photos, so the
     index is shared: picking a thumbnail also moves the mobile frame.
     ------------------------------------------------------------------ */

  function initPdp() {
    const pdp = document.querySelector('[data-taton-pdp]:not([data-taton-pdp-bound])');
    if (!pdp) return;
    pdp.setAttribute('data-taton-pdp-bound', '');

    /* --- Gallery --- */
    const images = Array.from(pdp.querySelectorAll('[data-taton-pdp-image]'));
    const dots = Array.from(pdp.querySelectorAll('[data-taton-pdp-dot]'));
    const thumbs = Array.from(pdp.querySelectorAll('[data-taton-pdp-thumb]'));
    const count = Math.max(dots.length, thumbs.length);

    // The page opens on the selected variant's photo (`pdp_start` in the section).
    const opened = images.find((el) => el.getAttribute('data-current') === 'true');
    let index = opened ? Number(opened.getAttribute('data-taton-pdp-image')) : 0;

    /* A video frame plays only while it is the current frame and actually on
       screen — the mobile and desktop galleries both hold a copy, and the one
       in the hidden gallery must not download a clip nobody sees. Under
       reduced motion it never starts by itself: the poster stays and the
       controls appear, so playing is her choice. */
    const syncVideos = () => {
      images.forEach((el) => {
        if (el.tagName !== 'VIDEO') return;
        const on = el.getAttribute('data-current') === 'true' && el.getClientRects().length > 0;
        if (!on) {
          el.pause();
          return;
        }
        if (reduceMotion.matches) {
          el.controls = true;
          return;
        }
        const p = el.play();
        /* NotAllowedError — the browser refuses autoplay: hand her the
           controls. AbortError — Chrome paused video in a hidden tab to save
           power (seen 30 Sept 2026): not a refusal, it resumes below when
           the page is visible again. */
        if (p && p.catch) p.catch((e) => { if (e && e.name === 'NotAllowedError') el.controls = true; });
      });
    };

    /* Frames more than one swipe away are `display: none` (the Gallery in
       sections/taton-main-product.liquid), so their photos wait; `near`
       moves with the current frame, one step ahead in either direction —
       the gallery wraps. A video's poster is armed the same way, and only
       in the gallery on screen: the hidden one fetches nothing. */
    const warm = () => {
      images.forEach((el) => {
        const n = Number(el.getAttribute('data-taton-pdp-image'));
        const near = n === (index + 1) % count || n === (index - 1 + count) % count;
        el.setAttribute('data-near', String(near));
        const poster = el.getAttribute('data-poster');
        if (poster && (near || n === index) && el.getClientRects().length) {
          el.poster = poster;
          el.removeAttribute('data-poster');
        }
      });
    };

    const show = (next) => {
      if (!count) return;
      index = (next + count) % count;
      images.forEach((img) => {
        const n = Number(img.getAttribute('data-taton-pdp-image'));
        img.setAttribute('data-current', String(n === index));
      });
      warm();
      dots.forEach((d, n) => d.setAttribute('data-current', String(n === index)));
      thumbs.forEach((t, n) => {
        t.setAttribute('aria-selected', String(n === index));
        if (n === index) t.scrollIntoView({ block: 'nearest' });
      });
      syncVideos();
    };

    if (images.some((el) => el.tagName === 'VIDEO')) {
      syncVideos();
      /* Posters a swipe away wait for `load`, off the critical path. */
      if (document.readyState === 'complete') warm();
      else window.addEventListener('load', warm, { once: true });
      window.addEventListener('resize', () => { warm(); syncVideos(); }, { passive: true });
      document.addEventListener('visibilitychange', () => { if (!document.hidden) syncVideos(); });
    }

    dots.forEach((d, n) => d.addEventListener('click', () => show(n)));
    thumbs.forEach((t, n) => t.addEventListener('click', () => show(n)));

    const swipeArea = pdp.querySelector('[data-taton-pdp-swipe]');
    if (swipeArea && count > 1) addPhotoZones(swipeArea, (dir) => show(index + dir));

    if (swipeArea && count > 1) {
      let startX = null;
      let startY = null;

      swipeArea.addEventListener(
        'touchstart',
        (e) => {
          startX = e.touches[0].clientX;
          startY = e.touches[0].clientY;
        },
        { passive: true }
      );

      swipeArea.addEventListener(
        'touchmove',
        (e) => {
          if (startX === null) return;
          const dx = e.touches[0].clientX - startX;
          const dy = e.touches[0].clientY - startY;
          // 44px, horizontal only — a vertical drag is the page scrolling.
          if (Math.abs(dx) > 44 && Math.abs(dx) > Math.abs(dy) * 1.4) {
            show(index + (dx < 0 ? 1 : -1));
            startX = null;
          }
        },
        { passive: true }
      );

      swipeArea.addEventListener('touchend', () => {
        startX = null;
      });
    }

    /* --- Heart, both copies kept in step — through the Saved store (module 14) --- */
    pdp.querySelectorAll('[data-taton-pdp-wish]').forEach((btn) => {
      savedBindHeart(btn, () => savedEntryFromPdp(pdp));
    });

    /* --- Sticky guard ---
       A sticky block taller than the viewport pins at the top and its
       bottom can never be scrolled to. Only stick while it fits; the
       accordions change that on every toggle. */
    const buy = pdp.querySelector('.taton-pdp__buy');
    if (buy) {
      const syncSticky = () => {
        const fits = buy.scrollHeight + chromeHeight() + 32 <= window.innerHeight;
        buy.style.position = fits ? '' : 'static';
      };
      syncSticky();
      window.addEventListener('resize', syncSticky, { passive: true });
      pdp.querySelectorAll('.taton-pdp__acc').forEach((acc) =>
        acc.addEventListener('toggle', syncSticky)
      );
    }

    /* --- Sticky mini add-to-bag ---
       Mobile only, past 620px of scroll. Horizon scrolls .page-wrapper, so
       the window offset alone would never move. */
    const mini = document.querySelector('[data-taton-pdp-mini]');
    const miniAdd = document.querySelector('[data-taton-pdp-mini-add]');
    const form = pdp.querySelector('[data-taton-pdp-form]');

    /* **Add to bag was a plain form post, so it left the page.**

       Every quick-add on a card goes through `cart/add.js` and opens the
       drawer; the product page's own button did not, so Shopify handled the
       POST and redirected to /cart. The customer was thrown out of the piece
       they were reading to a page they did not ask for — and the drawer, the
       tier bar and the gift picker, which are the whole point of the ladder,
       never appeared.

       Same route as the cards now: post as JSON, then dispatch `cart:update`,
       which the drawer already listens for and answers by re-rendering itself
       and opening. Nothing new is invented here; the form is simply joined to
       the plumbing that was already there. Without JavaScript the form still
       posts natively, which is the correct fallback. */
    if (form) {
      form.addEventListener('submit', (e) => {
        const id = form.querySelector('[name="id"]');
        if (!id || !id.value) return;

        e.preventDefault();
        if (form.hasAttribute('data-adding')) return;
        form.setAttribute('data-adding', '');

        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        const qtyEl = form.querySelector('[name="quantity"]');
        const quantity = Math.max(1, Number(qtyEl && qtyEl.value) || 1);

        // Whether Shopify has actually taken the line. Everything after this
        // point is presentation, and presentation failing must never be
        // answered by adding the piece a second time.
        let added = false;

        fetch(root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(id.value), quantity }] }),
        })
          .then((r) => {
            if (!r.ok) return Promise.reject(r);
            added = true;
            return fetch(root + 'cart.js');
          })
          .then((r) => r.json())
          .then((cart) => {
            document.querySelectorAll('[data-taton-cart-count]').forEach((el) => {
              el.textContent = cart.item_count;
            });
            document.dispatchEvent(new CustomEvent('cart:update', { detail: { cart } }));
          })
          .catch(() => {
            // A failed add must not strand the customer on a page that did
            // nothing: fall back to the native post, which at least lands
            // them somewhere true. But only when the add is what failed —
            // if the line is already in the bag and it was only the badge
            // read that broke, posting again would buy two.
            //
            // `HTMLFormElement.prototype.submit` rather than `form.submit`,
            // because a control named `submit` shadows the method on the
            // form and the fallback would throw instead of posting.
            if (!added) return HTMLFormElement.prototype.submit.call(form);
            document.dispatchEvent(new CustomEvent('cart:update', { detail: {} }));
          })
          .finally(() => form.removeAttribute('data-adding'));
      });
    }

    if (mini) {
      const wrapper = document.querySelector('.page-wrapper');
      const offset = () => Math.max(window.scrollY || 0, wrapper ? wrapper.scrollTop : 0);

      let shown = null;
      const syncMini = () => {
        const next = offset() > 620;
        if (next === shown) return;
        shown = next;
        mini.toggleAttribute('data-visible', next);
        mini.setAttribute('aria-hidden', String(!next));
        mini.inert = !next;
      };

      syncMini();
      window.addEventListener('scroll', syncMini, { passive: true });
      if (wrapper) wrapper.addEventListener('scroll', syncMini, { passive: true });

      if (miniAdd && form) {
        miniAdd.addEventListener('click', () => form.requestSubmit());
      }
    }

    /* --- Variants --- */
    const dataEl = document.querySelector('[data-taton-pdp-variants]');
    /* Option groups are chips now, not dropdowns — a `<fieldset>` of radios
       rather than a `<select>`. Both are read the same way here so a template
       that still renders a select keeps working. */
    const groups = Array.from(pdp.querySelectorAll('[data-taton-pdp-option]'));
    if (!dataEl || !groups.length) return;

    const valueOf = (g) =>
      g.tagName === 'SELECT' ? g.value : (g.querySelector('input:checked') || {}).value;

    let variants = [];
    try {
      variants = JSON.parse(dataEl.textContent);
    } catch (e) {
      return;
    }

    const idField = pdp.querySelector('[data-taton-pdp-variant-id]');
    const priceEl = pdp.querySelector('[data-taton-pdp-price]');
    const wasEl = pdp.querySelector('[data-taton-pdp-was]');
    const addBtn = pdp.querySelector('[data-taton-pdp-add]');

    // Follow the shop's own money format, or a switched variant would render
    // its price differently from the one Liquid printed on load.
    const format = dataEl.getAttribute('data-money-format') || '${{amount}}';

    // Mirrors Liquid's money_without_trailing_zeros, which is what the
    // template prints — a switched variant must not suddenly grow a ".00".
    // The markup in the format (taton.shop's is `<span class=money>…`) is
    // stripped inside `tatonMoney`: every use below writes with
    // `textContent`, and on 22 September that printed the tag as text on
    // every piece with more than one variant (found by Anton).
    const money = (cents) => tatonMoney(cents, format);

    /* Mark the finishes that cannot be bought, rather than removing them.
       A value is unavailable when no variant carrying it — with the other
       groups left as they are — is in stock. */
    const markUnavailable = (chosen) => {
      groups.forEach((g, gi) => {
        g.querySelectorAll('input[type="radio"]').forEach((input) => {
          const trial = chosen.slice();
          trial[gi] = input.value;
          const v = variants.find((x) => trial.every((value, i) => x.options[i] === value));
          const label = g.querySelector('label[for="' + input.id + '"]');
          if (!label) return;
          label.toggleAttribute('data-unavailable', !v || !v.available);
        });
      });
    };

    const sync = () => {
      const chosen = groups.map(valueOf);

      groups.forEach((g) => {
        const out = g.querySelector('[data-taton-pdp-option-value]');
        if (out) out.textContent = valueOf(g) || '';
      });
      markUnavailable(chosen);

      const match = variants.find((v) =>
        chosen.every((value, i) => v.options[i] === value)
      );

      if (!match) {
        if (addBtn) {
          addBtn.disabled = true;
          addBtn.textContent = TI.sold_out || 'Sold out';
        }
        if (miniAdd) {
          miniAdd.disabled = true;
          miniAdd.textContent = TI.sold_out || 'Sold out';
        }
        return;
      }

      if (idField) idField.value = match.id;
      if (priceEl) priceEl.textContent = money(match.price);

      /* **Show the finish that was chosen — 18 August 2026.**

         Choosing Silver moved the price, the button and the label and left the
         gold pair on screen, because until today no variant owned a
         photograph and there was nothing to switch to. Now that they do, the
         gallery follows.

         Matched on the filename rather than an id: the frames are drawn from
         `product.media` and carry media ids, while the variant JSON carries an
         *image* id. Two different id spaces for one photograph; the file name
         is the thing they agree on. */
      const vsrc = match.featured_image && match.featured_image.src;
      if (vsrc) {
        const vkey = vsrc.split('?')[0].split('/').pop().split('.')[0].split('_')[0].toLowerCase();
        const frame = images.findIndex((img) => img.getAttribute('data-photo-key') === vkey);
        if (frame >= 0 && frame !== index) show(frame);
      }

      const miniPrice = document.querySelector('[data-taton-pdp-mini-price]');
      if (miniPrice) miniPrice.textContent = money(match.price);
      if (miniAdd) {
        miniAdd.disabled = !match.available;
        miniAdd.textContent = match.available ? (TI.add || 'Add to bag') : (TI.sold_out || 'Sold out');
      }

      const onSale = match.compare_at_price && match.compare_at_price > match.price;
      if (wasEl) {
        wasEl.textContent = onSale ? money(match.compare_at_price) : '';
        wasEl.hidden = !onSale;
      }
      if (priceEl) priceEl.classList.toggle('taton-pdp__price--sale', Boolean(onSale));

      /* **The price span is looked up on every change, never kept — Anton, 13
         October 2026.** It was found once, at load. Choosing a sold-out size
         replaced the button's contents with «Немає в наявності», which took
         that span out of the page; choosing an in-stock size afterwards then
         wrote the price into the detached span and re-enabled the button, so
         it said «Немає в наявності» and added the T-shirt to the bag when
         pressed. A live lookup finds nothing in that state and rebuilds the
         label. */
      if (addBtn) {
        addBtn.disabled = !match.available;
        const addPrice = addBtn.querySelector('[data-taton-pdp-add-price]');
        if (!match.available) {
          addBtn.textContent = TI.sold_out || 'Sold out';
        } else if (addPrice) {
          addPrice.textContent = money(match.price);
        } else {
          addBtn.textContent = (TI.add || 'Add to bag') + ' — ';
          const span = document.createElement('span');
          span.className = 'taton-nums taton-money';
          span.setAttribute('data-taton-pdp-add-price', '');
          span.textContent = money(match.price);
          addBtn.appendChild(span);
        }
      }

      // Keep the URL honest so a refresh or a share lands on the same variant.
      const url = new URL(window.location.href);
      url.searchParams.set('variant', match.id);
      window.history.replaceState({}, '', url.toString());
    };

    // Radios bubble their change to the fieldset, so one listener per group
    // covers every chip inside it.
    groups.forEach((g) => g.addEventListener('change', sync));
    sync();
  }

  /* ------------------------------------------------------------------
     10. Cart

     Every mutation goes to Shopify and the section is re-rendered from the
     server. The reward ladder is computed in Liquid, so re-rendering keeps
     one source of truth — recomputing it here would be a second
     implementation to drift out of step.
     ------------------------------------------------------------------ */

  const cartRoot = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  /* Gift box auto-sync.

     Once the pieces clear the gift tier the box belongs in the bag, so the
     theme puts it there instead of leaving a button the customer has to
     notice. Two rules stop that from being presumptuous:

       1. A box the theme added carries `_taton_auto`, and only that box is
          taken back out if the subtotal falls under the tier again. A box
          the customer chose to pay for is theirs to keep.
       2. Removing a free box is remembered for the session. Without that,
          Remove would re-add it on the next render and read as broken.

     This reads the ladder Liquid already computed. It never recomputes a
     threshold — there is one implementation of the maths, in the snippet. */

  const BOX_DECLINED = 'taton:box-declined';

  const boxDeclined = () => {
    try {
      return window.sessionStorage.getItem(BOX_DECLINED) === '1';
    } catch (e) {
      return false;
    }
  };

  const setBoxDeclined = (on) => {
    try {
      if (on) window.sessionStorage.setItem(BOX_DECLINED, '1');
      else window.sessionStorage.removeItem(BOX_DECLINED);
    } catch (e) {
      /* Private mode. The box just stays a manual choice. */
    }
  };

  function readCartState(scope) {
    const el = scope.querySelector('[data-taton-cart-state]');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  /* One in flight at a time — the cart page and the drawer both call this
     on every render, and two adds racing would land two boxes. */
  let boxSyncing = false;

  /* Resolves true when the cart actually changed and the caller should
     re-render. */
  function syncGiftBox(scope) {
    if (boxSyncing) return Promise.resolve(false);

    const state = readCartState(scope);
    /* Either twin is enough. taton.shop sets only the free one (2 Oct 2026):
       the box is earned from 2 000 грн and nothing in the bag offers to sell
       it — setting the paid variant would add «Додати коробку зараз · 345 грн»
       to the ladder. Requiring the paid variant here meant the free box never
       went in at all (box test, 2 Oct). */
    if (!state || !(state.boxVariant || state.boxGiftVariant)) return Promise.resolve(false);

    const post = (path, body) => {
      boxSyncing = true;
      return fetch(cartRoot() + path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      })
        .then((r) => r.ok)
        .catch(() => false)
        .finally(() => {
          boxSyncing = false;
        });
    };

    const unlocked = !!(state.tiers && state.tiers[2]);

    /* **The gift is no longer taken back when a tier is lost — Anton, 20
       August 2026.**

       It used to be. Remove a piece after choosing a gift and the theme
       deleted the gift too, one line per pass, each pass costing a full
       section re-render — so the bag visibly reloaded twice for one click.
       *"Вона так дьоргається, наче перезавантажується… тепер по два рази
       завантажується."*

       His fix is better than a faster version of the old one: **let the piece
       stop being a gift and simply become something they are buying.** Nothing
       is deleted, so there is no second render and nothing vanishes out of the
       bag. Shopify has already stopped discounting the line, so the price the
       customer sees is the price they pay, and the drawer draws it as an
       ordinary line with an ordinary stepper the moment the discount lifts.

       Nobody gets anything free that they did not earn — that was the only
       thing the release was protecting, and the discount protects it by
       itself. The nudge turns the state into an offer: *"$40 more in pieces
       and your Baroque Pearl Hoops is on us."*

       The gift **box** is a different matter and still releases below: it is a
       zero-priced variant this theme adds, so nothing external takes it back,
       and a box nobody earned is a real loss. */

    /* The free box is a separate zero-priced variant, not a discounted one,
       because nothing in this store is ever discounted. So earning it means
       swapping SKUs rather than changing a price. */
    if (unlocked && state.boxGiftVariant) {
      if (!state.boxInCart && !boxDeclined()) {
        return post('cart/add.js', {
          items: [{ id: state.boxGiftVariant, quantity: 1, properties: { _taton_auto: 'true' } }],
        });
      }

      /* Bought the box before the tier was earned. Swap it for the free
         twin rather than leave the customer paying for something the
         ladder has since given them. */
      if (state.boxInCart && !state.boxIsFree && state.boxKey) {
        return post('cart/change.js', { id: state.boxKey, quantity: 0 }).then(() =>
          post('cart/add.js', {
            items: [{ id: state.boxGiftVariant, quantity: 1, properties: { _taton_auto: 'true' } }],
          })
        );
      }
    }

    /* Back under the tier holding a box nobody paid for: take it out rather
       than ship a gift that was not earned. A box they bought stays. */
    if (!unlocked && state.boxInCart && state.boxIsFree && state.boxKey) {
      return post('cart/change.js', { id: state.boxKey, quantity: 0 });
    }

    /* Under the tier again — let the offer come back next time it is
       earned rather than holding the refusal forever. */
    if (!unlocked && boxDeclined()) setBoxDeclined(false);

    return Promise.resolve(false);
  }

  /* The packing slip for gifts Shopify never hears about.

     Four of the five rungs above $100 are pool pieces put in the parcel by
     hand: no discount, no line, no Shopify object of any kind. So the promise
     has no proof behind it unless the theme writes one — and the trace has to
     live on the **cart**, not in the checkout form, because Apple Pay and Shop
     Pay go straight past the form. Order #1002 was paid that way.

     Liquid counts it, because Liquid is the only place that can see the tags
     it has to exclude; this posts it. The comparison against what is already
     on the cart is the whole loop guard — if the numbers agree, nothing is
     sent, and after a write they agree.

     Deliberately no re-render afterwards. The attribute is invisible to the
     customer, and re-rendering to show nothing is how a cart starts to look
     like it is reloading itself. `rewards-ladder-spec.md` §4, and
     `theme-patch-plan.md` §2.3.2 variant A. */

  let poolSyncing = false;

  function syncPoolAttributes(scope) {
    if (poolSyncing) return;

    const state = readCartState(scope);
    if (!state || typeof state.poolEarned !== 'number') return;

    const owed = state.poolEarned > 0 ? String(state.poolEarned) : '';
    const ladder = state.poolLadder || '';
    const haveOwed = state.attrGiftsOwed || '';
    const haveLadder = state.attrLadder || '';

    if (owed === haveOwed && ladder === haveLadder) return;

    poolSyncing = true;
    fetch(cartRoot() + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      /* An empty string is how Shopify deletes a cart attribute, so a bag
         that drops back under $120 clears the line rather than leaving a
         stale "Gifts owed: 2" on the order. */
      body: JSON.stringify({ attributes: { 'Gifts owed': owed, Ladder: ladder } }),
    })
      .catch(() => {})
      .finally(() => {
        poolSyncing = false;
      });
  }

  /* The pool gifts as a line the order carries — 6 September 2026.

     The attribute above is the record; this is what the packer sees. A
     zero-priced «Pool gift» variant is kept in the bag at exactly the number
     of pool gifts the ladder has earned, so the order — in the admin, in the
     duplicate the Ukrainian store gets, in the CRM — lists it as an ordinary
     line. Liquid counts, this posts; equal numbers mean no request, which is
     the loop guard. A line already in the bag is changed by key, so a
     customer who found the variant id and added five by hand is brought back
     to the earned number on the next render; the Flow guard on the order is
     the second net. flow-workflows.md §7.1. */
  let poolLineSyncing = false;

  function syncPoolLine(scope) {
    if (poolLineSyncing) return Promise.resolve(false);

    const state = readCartState(scope);
    if (!state || !state.poolVariant || typeof state.poolEarned !== 'number') return Promise.resolve(false);

    const want = state.poolEarned;
    const have = state.poolQty || 0;
    if (want === have) return Promise.resolve(false);

    const post = (path, body) => {
      poolLineSyncing = true;
      return fetch(cartRoot() + path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      })
        .then((r) => r.ok)
        .catch(() => false)
        .finally(() => {
          poolLineSyncing = false;
        });
    };

    if (have > 0 && state.poolKey) return post('cart/change.js', { id: state.poolKey, quantity: want });
    if (want > 0) {
      return post('cart/add.js', {
        items: [{ id: state.poolVariant, quantity: want, properties: { _taton_auto: 'true' } }],
      });
    }
    return Promise.resolve(false);
  }

  /* The receipt's button, and "Change" on a gift line. Both move the customer
     to a rail rather than to a page — П5: the gap used to be a sentence with
     nowhere to go.

     The target is looked up **inside the surface the button is in**. Both the
     cart page and the drawer are in the document at once on /cart, so the
     shelf exists twice; a button in the drawer that scrolled the page behind
     it would be worse than no button. That is also why the targets carry
     `data-taton-anchor` and not an id. */
  function bindScrollTo(scope) {
    scope.querySelectorAll('[data-taton-scroll-to]:not([data-taton-scroll-bound])').forEach((btn) => {
      btn.setAttribute('data-taton-scroll-bound', '');
      btn.addEventListener('click', () => {
        const name = btn.getAttribute('data-taton-scroll-to');
        const root = btn.closest('[data-taton-drawer], [data-taton-cart]') || document;
        const target = root.querySelector('[data-taton-anchor="' + name + '"]');
        if (!target) return;
        const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        target.scrollIntoView({ behavior: still ? 'auto' : 'smooth', block: 'start' });
      });
    });
  }

  /* Choosing and swapping gifts. Bound in both the cart page and the drawer,
     which keep their own busy flags and renderers. */
  function bindGiftPicker(scope, run) {
    scope.querySelectorAll('[data-taton-gift-add]').forEach((btn) => {
      btn.addEventListener('click', () => {
        run('cart/add.js', {
          // One of each. A gift is a choice, never a quantity.
          //
          // `_taton_gift` marks this as the theme's doing, so the auto-release
          // that hands a gift back when a tier is lost can tell it apart from
          // a Gift-edit piece the customer deliberately bought and is paying
          // for. Without the stamp it took both back.
          items: [
            {
              id: Number(btn.getAttribute('data-variant-id')),
              quantity: 1,
              properties: { _taton_gift: 'true' },
            },
          ],
        });
      });
    });

    scope.querySelectorAll('[data-taton-gift-remove]').forEach((btn) => {
      btn.addEventListener('click', () => {
        run('cart/change.js', { id: btn.getAttribute('data-line-key'), quantity: 0 });
      });
    });
  }

  /* Set by initCart on the cart page, so a quick-add anywhere on that page can
     redraw it the same way the cart's own buttons do. Null everywhere else. */
  let refreshCartPage = null;

  function initCart() {
    const cart = document.querySelector('[data-taton-cart]:not([data-taton-cart-bound])');
    if (!cart) return;
    cart.setAttribute('data-taton-cart-bound', '');

    const sectionId = cart.closest('.shopify-section')?.id.replace('shopify-section-', '');
    let busy = false;

    const rerender = () => {
      if (!sectionId) return window.location.reload();

      return fetch(cartRoot() + '?section_id=' + sectionId)
        .then((r) => r.text())
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const next = doc.querySelector('[data-taton-cart]');
          if (!next) return window.location.reload();
          cart.replaceWith(next);
          boot();
        });
    };

    refreshCartPage = rerender;

    const syncBadge = () =>
      fetch(cartRoot() + 'cart.js')
        .then((r) => r.json())
        .then((data) => {
          document.querySelectorAll('[data-taton-cart-count]').forEach((el) => {
            el.textContent = data.item_count;
          });
          document.dispatchEvent(new CustomEvent('cart:update', { detail: { cart: data } }));
        })
        .catch(() => {});

    bindScrollTo(cart);
    syncPoolAttributes(cart);

    /* Claim or release the free gift box before anything else reads the
       lines, so the customer never sees a bag that is one render behind. */
    syncGiftBox(cart)
      .then((changed) => syncPoolLine(cart).then((poolChanged) => changed || poolChanged))
      .then((changed) => {
        if (!changed) return;
        rerender();
        syncBadge();
        refreshDrawer();
      });

    const change = (body) => {
      if (busy) return;
      busy = true;
      cart.setAttribute('data-busy', '');

      return fetch(cartRoot() + 'cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      })
        .then((r) => (r.ok ? r.json() : Promise.reject(r)))
        .then(() => Promise.all([rerender(), syncBadge()]))
        .catch(() => {})
        .finally(() => {
          busy = false;
          cart.removeAttribute('data-busy');
        });
    };

    cart.querySelectorAll('[data-taton-qty]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const line = btn.closest('[data-line-key]');
        const valueEl = line.querySelector('[data-taton-qty-value]');
        const delta = Number(btn.getAttribute('data-taton-qty'));
        const next = Math.max(0, Number(valueEl.textContent.trim()) + delta);
        change({ id: line.getAttribute('data-line-key'), quantity: next });
      });
    });

    cart.querySelectorAll('[data-taton-line-remove]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const line = btn.closest('[data-line-key]');
        change({ id: line.getAttribute('data-line-key'), quantity: 0 });
      });
    });

    const boxRemove = cart.querySelector('[data-taton-box-remove]');
    if (boxRemove) {
      boxRemove.addEventListener('click', () => {
        /* Remember the refusal, or the auto-sync would put it straight
           back and Remove would look broken. */
        setBoxDeclined(true);
        const boxLine = boxRemove.closest('[data-line-key]');
        if (!boxLine) return;
        change({ id: boxLine.getAttribute('data-line-key'), quantity: 0 });
      });
    }

    const boxAdd = cart.querySelector('[data-taton-box-add]');
    if (boxAdd) {
      boxAdd.addEventListener('click', () => {
        if (busy) return;
        setBoxDeclined(false);
        busy = true;
        fetch(cartRoot() + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          // Fixed at one — the box is never a quantity decision.
          body: JSON.stringify({ items: [{ id: Number(boxAdd.getAttribute('data-variant-id')), quantity: 1 }] }),
        })
          .then((r) => (r.ok ? Promise.all([rerender(), syncBadge()]) : Promise.reject(r)))
          .catch(() => {})
          .finally(() => {
            busy = false;
          });
      });
    }

    bindGiftPicker(cart, (path, body) => {
      if (busy) return;
      busy = true;
      fetch(cartRoot() + path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      })
        .then((r) => (r.ok ? Promise.all([rerender(), syncBadge()]) : Promise.reject(r)))
        .catch(() => {})
        .finally(() => {
          busy = false;
        });
    });

    renderStockNotice(cart, 'cart');

    /* The reservation countdown that lived here was removed 2 September 2026
       along with its markup — a timer is pressure, and this cart's stated
       register is calm generosity. docs/cart-notes.md §9. */
  }

  /* ------------------------------------------------------------------
     11. Cart drawer

     Same rewards snippet as the cart page, so the two can never disagree.
     Re-rendered from the server after every change, like the page.
     ------------------------------------------------------------------ */

  function openDrawer() {
    const drawer = document.querySelector('[data-taton-drawer]');
    const scrim = document.querySelector('[data-taton-drawer-scrim]');
    if (!drawer) return;
    // Already open: nothing to animate, and re-locking the scroll on an
    // open drawer is how a second call turns into a visible flinch.
    if (drawer.hasAttribute('data-open')) return;
    drawer.toggleAttribute('data-open', true);
    drawer.setAttribute('aria-hidden', 'false');
    if (scrim) scrim.toggleAttribute('data-open', true);
    lockScroll(true);
  }

  function closeDrawer() {
    const drawer = document.querySelector('[data-taton-drawer]');
    const scrim = document.querySelector('[data-taton-drawer-scrim]');
    if (!drawer) return;
    drawer.removeAttribute('data-open');
    drawer.setAttribute('aria-hidden', 'true');
    if (scrim) scrim.removeAttribute('data-open');
    lockScroll(false);
  }

  /* One refresh at a time, and only the contents are swapped.

     **The drawer was re-opening, sometimes twice — Anton, 20 August 2026.**
     Two separate causes, both fixed here:

       1. `replaceWith` handed back the `<aside>` the server renders, which is
          always closed. The panel therefore slid shut and slid open again on
          every change, animation and all. Swapping `innerHTML` leaves the
          outer element — and its `data-open` — untouched, so an open drawer
          simply changes what it is showing.
       2. Two refreshes were racing. Quick-add called `refreshDrawer()` itself
          *and* dispatched `cart:update`, whose listener called it again, so
          one press fetched the section twice and opened the drawer twice. A
          call arriving while one is in flight now folds into it and re-runs
          once at the end, so the last state always wins without stacking. */
  let drawerRefresh = null;
  let drawerDirty = false;

  function refreshDrawer() {
    if (drawerRefresh) {
      drawerDirty = true;
      return drawerRefresh;
    }

    drawerRefresh = fetch(cartRoot() + '?section_id=taton-cart-drawer')
      .then((r) => r.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const next = doc.querySelector('[data-taton-drawer]');
        const current = document.querySelector('[data-taton-drawer]');
        if (!next || !current) return;
        current.innerHTML = next.innerHTML;
        // The guard lives on the outer element, which is no longer replaced,
        // so it has to be cleared for the new contents to be wired up.
        current.removeAttribute('data-taton-drawer-bound');
        bindDrawer();
        // The saved rail on an empty bag and the line under the ladder are
        // built onto whatever the server just sent (module 14).
        initSavedCart();
        /* **The new drawer arrived with dead buttons — Anton, 18 August 2026.**

           Only `bindDrawer` ran, which wires the drawer's own controls. Every
           product card inside it — the "goes with what you have" rail, its
           quick-add, its photo dots and hearts — is bound by `initCards`, and
           nothing called it on the replaced DOM. So quick-add worked exactly
           once: the first press added a piece and redrew the drawer, and the
           second press did nothing at all, on a button that still looked
           live. */
        initCards();
        initRails();
        // No re-open here any more. The panel never closed, so there is
        // nothing to put back.
      })
      .catch(() => {})
      .finally(() => {
        drawerRefresh = null;
        if (drawerDirty) {
          drawerDirty = false;
          refreshDrawer();
        }
      });

    return drawerRefresh;
  }

  function bindDrawer() {
    const drawer = document.querySelector('[data-taton-drawer]:not([data-taton-drawer-bound])');
    if (!drawer) return;
    drawer.setAttribute('data-taton-drawer-bound', '');

    drawer
      .querySelectorAll('[data-taton-drawer-close]')
      .forEach((b) => b.addEventListener('click', closeDrawer));

    let busy = false;
    const change = (body) => {
      if (busy) return;
      busy = true;
      fetch(cartRoot() + 'cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      })
        .then((r) => (r.ok ? r.json() : Promise.reject(r)))
        .then((data) => {
          document.querySelectorAll('[data-taton-cart-count]').forEach((el) => {
            el.textContent = data.item_count;
          });
          return refreshDrawer();
        })
        .catch(() => {})
        .finally(() => {
          busy = false;
        });
    };

    drawer.querySelectorAll('[data-taton-qty]').forEach((btn) =>
      btn.addEventListener('click', () => {
        const line = btn.closest('[data-line-key]');
        const value = line.querySelector('[data-taton-qty-value]');
        const next = Math.max(0, Number(value.textContent.trim()) + Number(btn.getAttribute('data-taton-qty')));
        change({ id: line.getAttribute('data-line-key'), quantity: next });
      })
    );

    drawer.querySelectorAll('[data-taton-line-remove]').forEach((btn) =>
      btn.addEventListener('click', () => {
        const line = btn.closest('[data-line-key]');
        change({ id: line.getAttribute('data-line-key'), quantity: 0 });
      })
    );

    const boxRemove = drawer.querySelector('[data-taton-box-remove]');
    if (boxRemove) {
      boxRemove.addEventListener('click', () => {
        setBoxDeclined(true);
        const boxLine = boxRemove.closest('[data-line-key]');
        if (!boxLine) return;
        change({ id: boxLine.getAttribute('data-line-key'), quantity: 0 });
      });
    }

    const boxAdd = drawer.querySelector('[data-taton-box-add]');
    if (boxAdd) {
      boxAdd.addEventListener('click', () => {
        setBoxDeclined(false);
        fetch(cartRoot() + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(boxAdd.getAttribute('data-variant-id')), quantity: 1 }] }),
        })
          .then(() => refreshDrawer())
          .catch(() => {});
      });
    }

    bindGiftPicker(drawer, (path, body) => {
      fetch(cartRoot() + path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      })
        .then(() => refreshDrawer())
        .catch(() => {});
    });

    bindScrollTo(drawer);
    syncPoolAttributes(drawer);
    renderStockNotice(drawer, 'drawer');

    /* Same claim-or-release pass the cart page runs. Whichever surface the
       customer is looking at, the rewards settle on their own. */
    syncGiftBox(drawer)
      .then((changed) => syncPoolLine(drawer).then((poolChanged) => changed || poolChanged))
      .then((changed) => {
        if (changed) refreshDrawer();
      });
  }

  function initDrawer() {
    bindDrawer();

    const scrim = document.querySelector('[data-taton-drawer-scrim]:not([data-taton-scrim-bound])');
    if (scrim) {
      scrim.setAttribute('data-taton-scrim-bound', '');
      scrim.addEventListener('click', closeDrawer);
    }

    document.querySelectorAll('[data-taton-drawer-open]:not([data-taton-open-bound])').forEach((btn) => {
      btn.setAttribute('data-taton-open-bound', '');
      btn.addEventListener('click', (e) => {
        if (!document.querySelector('[data-taton-drawer]')) return;
        e.preventDefault();
        openDrawer();
      });
    });

    if (!document.body.hasAttribute('data-taton-drawer-keys')) {
      document.body.setAttribute('data-taton-drawer-keys', '');
      document.addEventListener('keydown', (e) => {
        const drawer = document.querySelector('[data-taton-drawer][data-open]');
        if (e.key === 'Escape' && drawer) closeDrawer();
      });

      /* Quick-add opens the drawer, as the design calls for — but not on the
         cart page itself, where the change is already visible.

         It used to skip the refresh there as well, which left the drawer a
         version behind for the rest of the visit. Only the *opening* is
         conditional now: the contents are always brought up to date, and
         since the swap no longer touches the outer panel, refreshing a closed
         drawer is invisible. */
      document.addEventListener('cart:update', () => {
        const onCartPage = !!document.querySelector('[data-taton-cart]');
        refreshDrawer().then(() => {
          if (!onCartPage) openDrawer();
        });
      });
    }
  }

  /* ------------------------------------------------------------------
     12. Gift card

     The form is already correct without this: the amounts are radios named
     `id`, so Shopify receives the right variant whether or not the script
     runs. All this does is keep the printed figure on the card and the
     price on the button in step with the chosen amount.
     ------------------------------------------------------------------ */

  function initGiftCard() {
    const gift = document.querySelector('[data-taton-gift]:not([data-taton-gift-bound])');
    if (!gift) return;
    gift.setAttribute('data-taton-gift-bound', '');

    const figure = gift.querySelector('[data-taton-gift-figure]');
    const price = gift.querySelector('[data-taton-gift-price]');
    const radios = gift.querySelectorAll('[data-taton-gift-amount]');
    if (!radios.length) return;

    radios.forEach((radio) =>
      radio.addEventListener('change', () => {
        if (!radio.checked) return;
        const amount = radio.getAttribute('data-taton-gift-amount');
        if (figure) figure.textContent = amount;
        if (price) price.textContent = amount;
      })
    );
  }

  /* ------------------------------------------------------------------
     13. Pieces row drift

     Only when the merchant switches it on, and it stops for good the first
     time the customer touches the row. A strip that keeps sliding under a
     finger is the fastest way to make someone give up on it, and resuming
     after a pause would be worse — they would lose their place twice.
     ------------------------------------------------------------------ */

  function initPieceDrift() {
    const rails = document.querySelectorAll('[data-taton-rail-drift]:not([data-taton-drift-bound])');
    if (!rails.length) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    rails.forEach((rail) => {
      rail.setAttribute('data-taton-drift-bound', '');
      // The scrolling element is usually the rail itself; older markup wrapped
      // it in one, so a nested track still wins if there is one.
      const track = rail.querySelector('[data-taton-rail-track]') || rail;

      let stopped = false;
      const stop = () => {
        stopped = true;
      };
      ['pointerdown', 'wheel', 'touchstart', 'keydown'].forEach((type) => {
        track.addEventListener(type, stop, { once: true, passive: true });
      });

      let last = 0;
      const step = (now) => {
        if (stopped) return;
        if (last) {
          track.scrollLeft += (now - last) * 0.012;
          /* Reached the end: settle rather than snap back to the start. */
          if (track.scrollLeft >= track.scrollWidth - track.clientWidth - 1) stop();
        }
        last = now;
        window.requestAnimationFrame(step);
      };
      window.requestAnimationFrame(step);
    });
  }

  /* ------------------------------------------------------------------
     Boot
     ------------------------------------------------------------------ */

  /* ------------------------------------------------------------------
     Footer columns fold on a phone.

     They are rendered `open`, so a page without JavaScript keeps every link
     visible — this only ever closes them, and only where there is a chevron
     to open them again.
     ------------------------------------------------------------------ */

  function initFooterCols() {
    const cols = Array.from(document.querySelectorAll('[data-taton-footer-col]'));
    if (!cols.length) return;

    const narrow = window.matchMedia('(max-width: 759px)');
    const apply = () => cols.forEach((c) => (c.open = !narrow.matches));

    if (!document.body.hasAttribute('data-taton-footer-bound')) {
      document.body.setAttribute('data-taton-footer-bound', '');
      narrow.addEventListener('change', apply);
    }
    apply();
  }

  /* ------------------------------------------------------------------
     14. Saved — the heart, remembered  (docs/saved.md)

     What a customer saves lives in localStorage under `taton.saved`: no
     account, no app, no list on our side. Every heart on the page reads the
     one store and listens for one event, so a piece lit in a rail is lit in
     the grid, and stays lit after the drawer re-renders. Then the three
     places the list shows again: its own page, the empty bag, and the gap
     under the ladder — each built onto markup the server rendered, with
     every word coming from theme settings through the JSON that
     snippets/taton-saved-pill.liquid prints.

     Without localStorage (a private window that forbids it) the heart
     still works for the length of the page. Without JS it is what it was.
     ------------------------------------------------------------------ */

  const SAVED_KEY = 'taton.saved';
  const SAVED_MAX = 60;
  let savedItems = null;
  let savedConfig = null;
  let savedLastCount = null;

  function savedCfg() {
    if (savedConfig) return savedConfig;
    const el = document.querySelector('[data-taton-saved-config]');
    try {
      savedConfig = el ? JSON.parse(el.textContent) : {};
    } catch (e) {
      savedConfig = {};
    }
    return savedConfig;
  }

  function savedRead() {
    if (savedItems) return savedItems;
    let items = [];
    try {
      const data = JSON.parse(window.localStorage.getItem(SAVED_KEY) || 'null');
      if (data && data.v === 1 && Array.isArray(data.items)) {
        items = data.items.filter((i) => i && typeof i.h === 'string');
      }
    } catch (e) {
      /* nothing saved, or storage refused */
    }
    savedItems = items;
    return items;
  }

  function savedWrite(items) {
    savedItems = items.slice(0, SAVED_MAX);
    try {
      window.localStorage.setItem(SAVED_KEY, JSON.stringify({ v: 1, items: savedItems }));
    } catch (e) {
      /* keep it in memory for this page at least */
    }
    document.dispatchEvent(new CustomEvent('taton:saved', { detail: { items: savedItems } }));
  }

  function savedHas(handle) {
    return savedRead().some((i) => i.h === handle);
  }

  // Newest first, so the list reads in the order she found things.
  function savedAdd(entry) {
    const items = savedRead().filter((i) => i.h !== entry.h);
    items.unshift(Object.assign({ t: Date.now() }, entry));
    savedWrite(items);
  }

  function savedRemove(handle) {
    savedWrite(savedRead().filter((i) => i.h !== handle));
  }

  /* Money the way Liquid's money_without_trailing_zeros prints it, from the
     shop's own format, so the sum line and the bag never disagree on "$42". */
  function savedMoney(cents) {
    return tatonMoney(cents, savedCfg().moneyFormat || '${{amount}}');
  }

  function savedEscape(text) {
    return String(text).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);
  }

  /* One event per press for the pixel — GA4's add_to_wishlist is the demand
     signal Anton reads, and nothing on the storefront ever shows the count. */
  function savedPublish(entry, saved) {
    try {
      const a = window.Shopify && window.Shopify.analytics;
      if (a && typeof a.publish === 'function') {
        a.publish('taton_saved', {
          handle: entry.h,
          id: entry.id,
          title: entry.n,
          price: entry.p / 100,
          currency: savedCfg().currency || (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD',
          saved,
        });
      }
    } catch (e) {
      /* analytics never breaks the heart */
    }
  }

  function savedThumb(img) {
    if (!img) return '';
    // On a product page the first `img` can be the copy in the gallery hidden
    // at this width, whose current source is an inline blank — the `src`
    // attribute still names the photo (US window, 7 Oct 2026).
    const current = img.currentSrc && !img.currentSrc.startsWith('data:') ? img.currentSrc : '';
    const src = current || img.src || '';
    return src.replace(/([?&])width=\d+/, '$1width=240');
  }

  function savedEntryFromCard(card) {
    return {
      h: card.getAttribute('data-taton-card-handle') || '',
      id: Number(card.getAttribute('data-taton-card-id')) || 0,
      v: Number(card.getAttribute('data-taton-card-variant')) || 0,
      p: Number(card.getAttribute('data-taton-card-price')) || 0,
      n: card.getAttribute('data-taton-card-title') || '',
      i: savedThumb(card.querySelector('img')),
      g: card.hasAttribute('data-taton-card-gift') ? 1 : 0,
      a: card.getAttribute('data-taton-card-available') === '0' ? 0 : 1,
    };
  }

  function savedEntryFromPdp(pdp) {
    // The variant the form would add right now, not the one the page loaded with.
    const idInput = pdp.querySelector('[data-taton-pdp-form] [name="id"]');
    return {
      h: pdp.getAttribute('data-taton-pdp-handle') || '',
      id: Number(pdp.getAttribute('data-taton-pdp-id')) || 0,
      v: Number(idInput && idInput.value) || Number(pdp.getAttribute('data-taton-pdp-variant')) || 0,
      p: Number(pdp.getAttribute('data-taton-pdp-price')) || 0,
      n: pdp.getAttribute('data-taton-pdp-title') || '',
      i: savedThumb(pdp.querySelector('img')),
      g: pdp.hasAttribute('data-taton-pdp-gift') ? 1 : 0,
      a: pdp.getAttribute('data-taton-pdp-available') === '0' ? 0 : 1,
    };
  }

  function savedPaint(btn, on) {
    btn.setAttribute('aria-pressed', String(on));
    const glyph = btn.querySelector('span');
    if (glyph) glyph.textContent = on ? '♥' : '♡';
  }

  function savedBindHeart(btn, entryFn) {
    if (btn.hasAttribute('data-taton-saved-bound')) return;
    btn.setAttribute('data-taton-saved-bound', '');
    savedPaint(btn, savedHas(entryFn().h));
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      const entry = entryFn();
      if (!entry.h) return;
      const on = !savedHas(entry.h);
      if (on) savedAdd(entry);
      else savedRemove(entry.h);
      savedPublish(entry, on);
      if (on) {
        savedToast('first');
        const at = Number(savedCfg().shareAt) || 3;
        if (savedRead().length >= at) savedToast('share');
      }
    });
  }

  /* The share toast's link opens the phone's own sheet when there is one;
     the page is the fallback and what a desktop gets. Bound once, on the
     document, because the toast is one element that changes its words. */
  document.addEventListener('click', (e) => {
    const link = e.target.closest && e.target.closest('[data-taton-saved-toast-share]');
    if (!link) return;
    savedPublishShare('toast');
    if (!navigator.share) return;
    e.preventDefault();
    navigator.share({ title: document.title, url: link.href }).catch(() => {});
  });

  // Every heart on the page, bound or freshly rendered, painted from the store.
  function savedSyncHearts() {
    document.querySelectorAll('[data-taton-card]').forEach((card) => {
      const h = card.getAttribute('data-taton-card-handle');
      const btn = card.querySelector('[data-taton-card-heart]');
      if (h && btn) savedPaint(btn, savedHas(h));
    });
    const pdp = document.querySelector('[data-taton-pdp]');
    if (pdp) {
      const on = savedHas(pdp.getAttribute('data-taton-pdp-handle'));
      pdp.querySelectorAll('[data-taton-pdp-wish]').forEach((btn) => savedPaint(btn, on));
    }
  }

  /* The list as a URL — the same one the Kept page copies and shares. */
  function savedListUrl() {
    const u = new URL(savedCfg().url || '/', window.location.origin);
    const handles = savedRead().map((i) => i.h);
    if (handles.length) u.searchParams.set('p', handles.join(','));
    return u.toString();
  }

  /* Two toasts a session, and never more — Anton, 7 September 2026, with
     the risk said out loud: two in a row reads as a nudge, so each shows
     once and they are not the same sentence.

       first   the first save of a session: the name, and "See all"
       share   the save that brings the list to `shareAt` pieces: the
               count, and "Send the list" — which on a phone opens the
               share sheet straight from the toast, because that is the
               moment a list becomes worth sending

     After those the pinned count is the feedback. */
  function savedToast(kind) {
    const key = kind === 'share' ? 'taton.saved.toast.share' : 'taton.saved.toast';
    let shown = false;
    try {
      shown = window.sessionStorage.getItem(key) === '1';
    } catch (e) {
      /* fine */
    }
    if (shown) return;
    const toast = document.querySelector('[data-taton-saved-toast]');
    if (!toast) return;
    try {
      window.sessionStorage.setItem(key, '1');
    } catch (e) {
      /* fine */
    }

    const cfg = savedCfg();
    const text = toast.querySelector('[data-taton-saved-toast-text]');
    const link = toast.querySelector('[data-taton-saved-toast-link]');
    const n = savedRead().length;
    if (kind === 'share') {
      if (text) text.textContent = (cfg.shareText || '[count] kept').replace('[count]', String(n));
      if (link) {
        link.textContent = cfg.shareLink || 'Send the list';
        link.href = savedListUrl();
        link.setAttribute('data-taton-saved-toast-share', '');
      }
    } else {
      if (text) text.textContent = cfg.toastText || cfg.label || TI.kept || 'Kept';
      if (link) {
        link.textContent = cfg.toastLink || 'Your list';
        link.href = cfg.url || '/';
        link.removeAttribute('data-taton-saved-toast-share');
      }
    }

    toast.hidden = false;
    // A timeout, not requestAnimationFrame: rAF is paused in a background
    // tab, and a heart pressed just before switching away would leave the
    // toast waiting at opacity 0 until the tab came back.
    window.setTimeout(() => toast.setAttribute('data-in', ''), 20);
    window.setTimeout(() => {
      toast.removeAttribute('data-in');
      window.setTimeout(() => {
        toast.hidden = true;
      }, reduceMotion.matches ? 0 : 320);
    }, 2400);
  }

  /* Every count and every heart glyph outside the cards — the header, the
     menu line, whatever carries the attributes. Hidden at zero, filled and
     Ember above it. (The pinned count at the bottom of the page is gone —
     Anton, 7 September 2026: "такі літаючі кнопки мені не подобаються в
     магазині"; the header carries the heart now.) */
  function savedSyncCounts() {
    const n = savedRead().length;
    document.querySelectorAll('[data-taton-saved-count]').forEach((el) => {
      el.textContent = String(n);
      el.hidden = n === 0;
    });
    document.querySelectorAll('[data-taton-kept-glyph]').forEach((glyph) => {
      // The drawn heart (snippets/taton-icon-heart.liquid) fills through
      // CSS on data-on; a text glyph, where one is left, swaps characters.
      if (glyph.tagName.toLowerCase() !== 'svg') glyph.textContent = n > 0 ? '♥' : '♡';
      glyph.toggleAttribute('data-on', n > 0);
    });
    // The announcement bar's Kept message — board 2d, 8 September 2026:
    // the way to her list rides in the band that carries the ladder, and
    // only once there is a list. Words from the element's own attributes;
    // module 5 reads the visible messages on every turn, so lifting
    // `hidden` here is all it takes to put it into the rotation.
    document.querySelectorAll('[data-taton-promo-kept]').forEach((msg) => {
      const text = msg.querySelector('[data-taton-promo-kept-text]');
      if (n > 0 && text) {
        const words =
          n === 1
            ? msg.getAttribute('data-text-one') || ''
            : (msg.getAttribute('data-text-full') || '').replace('[count]', String(n));
        text.textContent = words;
        msg.hidden = false;
      } else {
        msg.hidden = true;
        msg.removeAttribute('data-current');
      }
    });
    // Tell the band whether this was a heart just pressed (the count went
    // up on this page) or a page arriving with a list; module 5 decides
    // what to do with each. The first sync of a page is never "increased".
    const increased = savedLastCount !== null && n > savedLastCount;
    savedLastCount = n;
    document.dispatchEvent(new CustomEvent('taton:kept-promo', { detail: { count: n, increased } }));
    // The menu line's hint counts once there is something to count. Words
    // come from the element's own attributes.
    document.querySelectorAll('[data-taton-menu-kept]').forEach((item) => {
      const hint = item.querySelector('[data-taton-menu-kept-hint]');
      if (!hint) return;
      let words = item.getAttribute('data-hint-empty') || '';
      if (n === 1) words = item.getAttribute('data-hint-one') || words;
      else if (n > 1) words = (item.getAttribute('data-hint-full') || words).replace('[count]', String(n));
      hint.textContent = words;
    });
  }

  /* One event per send — GA4's standard `share`, with how it was sent. This
     is the number the whole list exists for (brand-aura.md §1: the one free
     channel is "хочеться розповісти"), and until 7 September nothing counted it. */
  function savedPublishShare(method, count) {
    try {
      const a = window.Shopify && window.Shopify.analytics;
      if (a && typeof a.publish === 'function') {
        a.publish('taton_shared', { method, count: typeof count === 'number' ? count : savedRead().length });
      }
    } catch (e) {
      /* analytics never breaks the link */
    }
  }

  /* Several variants in one request, the way quick-add adds one. */
  function savedAddAllToBag(variantIds, gift) {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const props = gift ? { properties: { [gift.key]: gift.value } } : {};
    return fetch(root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: variantIds.map((id) => Object.assign({ id: Number(id), quantity: 1 }, props)) }),
    })
      .then((r) => (r.ok ? fetch(root + 'cart.js') : Promise.reject(r)))
      .then((r) => r.json())
      .then((cart) => {
        document.querySelectorAll('[data-taton-cart-count]').forEach((el) => {
          el.textContent = cart.item_count;
        });
        document.dispatchEvent(new CustomEvent('cart:update', { detail: { cart } }));
        if (typeof refreshCartPage === 'function') refreshCartPage();
      });
  }

  document.addEventListener('taton:saved', () => {
    savedSyncHearts();
    savedSyncCounts();
  });

  /* The real card for one saved piece, rendered by the server through
     sections/taton-saved-card.liquid. Null when the piece is gone. */
  function savedFetchCard(handle) {
    const base = savedCfg().cardEndpoint || '/products/';
    return fetch(base + encodeURIComponent(handle) + '?section_id=taton-saved-card', {
      headers: { Accept: 'text/html' },
    })
      .then((r) => (r.ok ? r.text() : ''))
      .then((html) => {
        if (!html) return null;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const wrap = doc.querySelector('[data-taton-saved-item]');
        const card = wrap && wrap.querySelector('[data-taton-card]');
        if (!wrap || !card) return null;
        return {
          card,
          h: wrap.getAttribute('data-taton-saved-item'),
          id: Number(wrap.getAttribute('data-id')) || 0,
          v: Number(wrap.getAttribute('data-variant')) || 0,
          p: Number(wrap.getAttribute('data-price')) || 0,
          n: wrap.getAttribute('data-title') || '',
          available: wrap.getAttribute('data-available') === '1',
          g: wrap.hasAttribute('data-gift') ? 1 : 0,
          promo: wrap.hasAttribute('data-promo') ? 1 : 0,
        };
      })
      .catch(() => null);
  }

  /* Add one variant, the way quick-add does: the event is the single route
     to the drawer, the cart page redraws itself. */
  function savedAddToBag(variantId) {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return fetch(root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
    })
      .then((r) => (r.ok ? fetch(root + 'cart.js') : Promise.reject(r)))
      .then((r) => r.json())
      .then((cart) => {
        document.querySelectorAll('[data-taton-cart-count]').forEach((el) => {
          el.textContent = cart.item_count;
        });
        document.dispatchEvent(new CustomEvent('cart:update', { detail: { cart } }));
        if (typeof refreshCartPage === 'function') refreshCartPage();
      });
  }

  /* --- The page ------------------------------------------------------ */

  function savedRenderSum(root, el, found) {
    if (!el) return;
    // Pieces only: a Gift edit piece is $0 on the ladder, a sold-out one
    // cannot be bought. From two up — "all of it" is not a sentence about one thing.
    const pieces = found.filter((f) => f.available && !f.g);
    if (pieces.length < 2) {
      el.hidden = true;
      return;
    }
    // **Як кошик — 23 вересня 2026.** Акція 1+1=3 (data-promo-buy на корені;
    // без нього — американська збірка, акції немає) знімає найдешевшу з кожних
    // трьох речей акції, тож сума й проміжок до доставки рахуються вже після
    // неї, як `cart.total_price`. Пакування — від суми самих речей акції після
    // знижки, коли `data-box-basis="promo_paid"` (settings.box_basis).
    const promoBuy = Number(root.getAttribute('data-promo-buy')) || 0;
    const promoPrices = pieces.filter((f) => f.promo).map((f) => f.p).sort((a, b) => a - b);
    const freeCount = promoBuy ? Math.floor(promoPrices.length / (promoBuy + 1)) : 0;
    const promoOff = promoPrices.slice(0, freeCount).reduce((a, p) => a + p, 0);
    const total = pieces.reduce((a, f) => a + f.p, 0) - promoOff;
    const promoPaid = promoPrices.reduce((a, p) => a + p, 0) - promoOff;
    const boxBasis = root.getAttribute('data-box-basis') === 'promo_paid' ? promoPaid : total;
    const t1 = (Number(root.getAttribute('data-tier-shipping')) || 0) * 100;
    const t2 = (Number(root.getAttribute('data-gift-every')) || 0) * 100;
    const t3 = (Number(root.getAttribute('data-tier-box')) || 0) * 100;
    const first = Math.max(t1, t2);
    let text;
    if (t3 && boxBasis >= t3 && total >= first) text = root.getAttribute('data-sum-box');
    else if (first && total >= first) text = root.getAttribute('data-sum-reached');
    else text = root.getAttribute('data-sum-short');
    if (!text) {
      el.hidden = true;
      return;
    }
    // Сума менша за ціни на картках, коли спрацювала акція, — сказати чому.
    const promoNote = promoOff > 0 && TI.promo_applied ? ' ' + savedEscape(TI.promo_applied) : '';
    el.innerHTML = savedEscape(text)
      .replace('[sum]','<strong class="taton-nums">' + savedMoney(total) + '</strong>' + promoNote)
      .replace('[gap]','<strong class="taton-nums">' + savedMoney(Math.max(0, first - total)) + '</strong>');
    el.hidden = false;
  }

  function savedRenderRecs(section, track, seeds, exclude) {
    if (!section || !track || !seeds.length) return;
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const ask = (seed) =>
      seed.id
        ? fetch(
            root +
              'recommendations/products?section_id=taton-saved-recs&product_id=' +
              seed.id +
              '&limit=6&intent=related'
          )
            .then((r) => (r.ok ? r.text() : ''))
            .catch(() => '')
        : Promise.resolve('');

    Promise.all(seeds.map(ask)).then((htmls) => {
      const seen = new Set(exclude);
      const cards = [];
      const lists = htmls.map((html) =>
        Array.from(new DOMParser().parseFromString(html, 'text/html').querySelectorAll('[data-taton-saved-rec]'))
      );
      // Round-robin across the seeds, so one piece does not fill the rail alone.
      for (let i = 0; cards.length < 8; i += 1) {
        let any = false;
        lists.forEach((list) => {
          const item = list[i];
          if (!item) return;
          any = true;
          // The handle keeps out what is already on the list; the key (handle
          // and colour, 13 Oct 2026) keeps out true duplicates, so two colours
          // of one piece both get through.
          const h = item.getAttribute('data-taton-saved-rec');
          const key = item.getAttribute('data-taton-saved-rec-key') || h;
          const card = item.querySelector('[data-taton-card]');
          if (h && card && !seen.has(h) && !seen.has(key) && cards.length < 8) {
            seen.add(key);
            cards.push(card);
          }
        });
        if (!any) break;
      }
      if (!cards.length) return;
      cards.forEach((card) => {
        const item = document.createElement('div');
        item.className = 'taton-rail-section__item';
        item.appendChild(card);
        track.appendChild(item);
      });
      section.hidden = false;
      initCards();
      initRails();
      savedSyncHearts();
    });
  }

  function initSavedPage() {
    const root = document.querySelector('[data-taton-saved]:not([data-taton-saved-bound])');
    if (!root) return;
    root.setAttribute('data-taton-saved-bound', '');

    const grid = root.querySelector('[data-taton-saved-grid]');
    const empty = root.querySelector('[data-taton-saved-empty]');
    const tools = root.querySelector('[data-taton-saved-tools]');
    const note = root.querySelector('[data-taton-saved-note]');
    const count = root.querySelector('[data-taton-saved-page-count]');
    const sum = root.querySelector('[data-taton-saved-sum]');
    const title = root.querySelector('[data-taton-saved-title]');
    const eyebrow = root.querySelector('[data-taton-saved-eyebrow]');
    const keep = root.querySelector('[data-taton-saved-keep]');
    const share = root.querySelector('[data-taton-saved-share]');
    const copy = root.querySelector('[data-taton-saved-copy]');
    const recs = root.querySelector('[data-taton-saved-recs]');
    const recsTrack = root.querySelector('[data-taton-saved-recs-track]');
    const tags = root.querySelector('[data-taton-saved-tags]');
    const mail = root.querySelector('[data-taton-saved-mail]');

    // A list someone sent: the URL carries it and the store is not touched
    // until she says "Keep these".
    const params = new URLSearchParams(window.location.search);
    const sharedParam = params.get('p');
    const shared = sharedParam !== null;
    const raw = shared
      ? sharedParam
          .split(',')
          .map((h) => h.trim().toLowerCase())
          // Letters, not ASCII letters. The guard belongs here — a handle
          // goes into a URL path — and `[a-z0-9-]` did guard it, by throwing
          // away every handle on this shop, all of which are Cyrillic. A list
          // someone was sent read «Поки що порожньо» however many pieces it
          // carried. Measured 16 September 2026; `\p{L}` keeps the guard and
          // lets the alphabet through.
          // And not letters only either — 6 October 2026: many handles here
          // end in an emoji («…-ніжна-квітка-🌸»), and `\p{L}\p{N}` dropped
          // every one of them from a list a friend was sent, silently (3 sent,
          // 2 shown). Anything but URL structure is allowed: no whitespace,
          // slash, query, fragment, ampersand or dot-dot.
          .filter((h) => h && !/[\s/?#&\\]|\.\./u.test(h))
      : savedRead().map((i) => i.h);
    const handles = raw.filter((h, i) => raw.indexOf(h) === i).slice(0, SAVED_MAX);

    if (eyebrow) eyebrow.hidden = !shared;
    // A sent list is named in the heading — "A list someone sent you" —
    // and the pocket's name becomes its eyebrow (board 1g, 8 September 2026).
    // Since 6 October 2026 the sender can name it herself («Оля, на день
    // народження»); that name, carried as `t=`, wins over the stock heading.
    const sentName = shared ? (params.get('t') || '').replace(/\s+/g, ' ').trim().slice(0, 60) : '';
    if (title && shared) {
      if (sentName) title.textContent = sentName;
      else if (title.getAttribute('data-shared')) title.textContent = title.getAttribute('data-shared');
    }

    // Her own list: the name field, kept in this browser between visits.
    const NAME_KEY = 'taton.saved.name';
    const nameWrap = root.querySelector('[data-taton-saved-name-wrap]');
    const nameInput = root.querySelector('[data-taton-saved-name]');
    if (nameWrap) nameWrap.hidden = shared;
    if (nameInput && !shared) {
      try {
        nameInput.value = window.localStorage.getItem(NAME_KEY) || '';
      } catch (e) {
        /* no storage — the field still works for this page */
      }
      nameInput.addEventListener('input', () => {
        try {
          window.localStorage.setItem(NAME_KEY, nameInput.value.slice(0, 60));
        } catch (e) {
          /* ignore */
        }
      });
    }
    if (keep) keep.hidden = !shared;
    // The heaviest word in the bar is the one this list is for: Keep these
    // on a sent list; on her own, Send the list, or Copy link where the
    // browser has no share sheet.
    if (keep && shared) keep.classList.add('taton-saved__tool--primary');

    // A sent list says the hearts on it start one of her own, and points to
    // hers when she already has one.
    const sharedNote = root.querySelector('[data-taton-saved-shared]');
    const yours = root.querySelector('[data-taton-saved-yours]');
    if (sharedNote) sharedNote.hidden = !shared;
    if (yours) yours.hidden = !(shared && savedRead().length > 0);

    // "Add all to the bag" — a sent list only, and only the pieces that can
    // be bought. Filled in once the cards have answered, below.
    const addAll = root.querySelector('[data-taton-saved-addall]');
    let buyable = [];
    if (addAll) {
      addAll.addEventListener('click', () => {
        if (!buyable.length) return;
        addAll.disabled = true;
        savedAddAllToBag(buyable, window.tatonGiftFor)
          .then(() => {
            const open = document.querySelector('[data-taton-drawer-open]');
            if (open) open.click();
          })
          .catch(() => {})
          .finally(() => {
            addAll.disabled = false;
          });
      });
    }

    const showEmpty = () => {
      if (empty) empty.hidden = false;
      if (tools) tools.hidden = true;
      if (note) note.hidden = true;
      if (count) count.hidden = true;
      if (sum) sum.hidden = true;
      if (addAll) addAll.hidden = true;
      /* The address is worth leaving with nothing kept — Anton, 8 September
         2026: it is not only for "this one is back". With an empty list there
         is nothing to tag, so it subscribes and that is all; the words say
         both halves. */
      if (tags) tags.value = '';
      if (mail) mail.hidden = false;
    };

    if (!handles.length || !grid) {
      showEmpty();
      return;
    }

    // Placeholders first, so the grid has its shape before a card arrives.
    const slots = handles.map(() => {
      const slot = document.createElement('div');
      slot.className = 'taton-grid__item';
      slot.innerHTML = '<div class="taton-saved__placeholder"></div>';
      grid.appendChild(slot);
      return slot;
    });
    if (tools) tools.hidden = false;
    if (note) note.hidden = false;

    Promise.all(handles.map(savedFetchCard)).then((results) => {
      const found = [];
      results.forEach((res, i) => {
        if (!res) {
          slots[i].remove();
          return;
        }
        slots[i].innerHTML = '';
        slots[i].appendChild(res.card);
        found.push(res);
      });

      if (!shared) {
        // The store follows what the shop says today: a piece that is gone
        // leaves the list, prices refresh; her order stays.
        const before = JSON.stringify(savedRead());
        const live = savedRead()
          .filter((i) => found.some((f) => f.h === i.h))
          .map((i) => {
            const f = found.find((x) => x.h === i.h);
            return Object.assign({}, i, {
              id: f.id || i.id,
              v: f.v || i.v,
              p: f.p,
              n: f.n || i.n,
              g: f.g,
              a: f.available ? 1 : 0,
            });
          });
        if (JSON.stringify(live) !== before) savedWrite(live);
      }

      if (!found.length) {
        showEmpty();
        return;
      }

      if (count) {
        // "4 pieces", in the bar, the way a collection counts itself.
        // Three forms, by the last two digits: 1, 21 — one; 2–4, 22–24 — few;
        // 5–20, 25–30 — many. «2 речей» was on the Ukrainian page until
        // 23 September 2026 (E066). An empty few falls back to many, so a
        // two-form language reads as before.
        const n = found.length;
        const d10 = n % 10, d100 = n % 100;
        const one = root.getAttribute('data-piece-one') || 'piece';
        const many = root.getAttribute('data-piece-many') || 'pieces';
        const few = root.getAttribute('data-piece-few') || many;
        const word =
          d10 === 1 && d100 !== 11 ? one
            : d10 >= 2 && d10 <= 4 && (d100 < 12 || d100 > 14) ? few
            : many;
        count.textContent = n + ' ' + word;
        count.hidden = false;
      }
      buyable = found.filter((f) => f.available && f.v && !f.g).map((f) => f.v);
      if (addAll) addAll.hidden = !(shared && buyable.length > 0);
      initCards();
      // On a list a friend sent, buying a piece is giving it (6 Oct 2026).
      // Pieces added from this grid carry «gift for: <list name>» as a line
      // property; the bag then offers a card (see initGiftNote).
      const giftKey = root.getAttribute('data-gift-for-key');
      if (shared && giftKey) {
        window.tatonGiftFor = { key: giftKey, value: sentName || (title && title.textContent.trim()) || '—' };
      }
      const giftLabel = root.getAttribute('data-gift-label');
      if (shared && giftLabel) {
        grid.querySelectorAll('button.taton-card__add:not([disabled]):not(.taton-card__add--quiet)').forEach((b) => {
          b.textContent = giftLabel;
        });
      }
      savedSyncHearts();
      savedRenderSum(root, sum, found);
      /* **The tags come from her own store, never from a list she was sent.**
         A `saved:` tag is a promise to write when that piece is back, and
         somebody else's choices are not hers to be promised about — so on
         `?p=` the field goes out empty.

         The form itself now stands on all three states — Anton, 8 September
         2026, on it being narrower than it needs to be. An address left here
         also buys the letters, which is worth leaving with an empty list or
         on a list a friend sent; with no tags it is exactly a subscribe and
         nothing more, and the words under the field say so. */
      if (tags) tags.value = shared ? '' : found.map((f) => 'saved:' + f.h).join(',');
      if (mail) mail.hidden = false;
      savedRenderRecs(
        recs,
        recsTrack,
        found.slice(0, 3),
        found.map((f) => f.h)
      );
    });

    const listUrl = () => {
      const u = new URL(savedCfg().url || window.location.pathname, window.location.origin);
      u.searchParams.set('p', (shared ? handles : savedRead().map((i) => i.h)).join(','));
      const name = shared ? sentName : nameInput ? nameInput.value.replace(/\s+/g, ' ').trim().slice(0, 60) : '';
      if (name) u.searchParams.set('t', name);
      return u.toString();
    };

    if (copy && !shared && !navigator.share) copy.classList.add('taton-saved__tool--primary');

    if (share && navigator.share) {
      share.hidden = false;
      if (!shared) share.classList.add('taton-saved__tool--primary');
      share.addEventListener('click', () => {
        // The list being sent — hers, or the one she was sent and is passing on.
        savedPublishShare('native', shared ? handles.length : savedRead().length);
        const named = new URL(listUrl()).searchParams.get('t');
        navigator.share({ title: named || document.title, url: listUrl() }).catch(() => {});
      });
    }

    if (copy) {
      copy.addEventListener('click', () => {
        savedPublishShare('copy', shared ? handles.length : savedRead().length);
        const label = copy.textContent;
        const done = () => {
          copy.textContent = root.getAttribute('data-copied') || label;
          window.setTimeout(() => {
            copy.textContent = label;
          }, 1600);
        };
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(listUrl()).then(done, done);
        } else {
          window.prompt('', listUrl());
        }
      });
    }

    if (keep) {
      keep.addEventListener('click', () => {
        // Hers first, then what was sent that she did not have.
        const mine = savedRead();
        const add = [];
        grid.querySelectorAll('[data-taton-card]').forEach((card) => {
          const h = card.getAttribute('data-taton-card-handle');
          if (h && !mine.some((i) => i.h === h) && !add.some((i) => i.h === h)) {
            add.push(Object.assign({ t: Date.now() }, savedEntryFromCard(card)));
          }
        });
        savedWrite(mine.concat(add));
        window.location.href = savedCfg().url || window.location.pathname;
      });
    }
  }

  /* --- The bag ------------------------------------------------------- */

  /* --- The bag: kept pieces are a rank, not a block --------------------

     **Rewritten 7 September 2026, and the reason is worth keeping.** The
     first version put her list in the bag twice more: a line under the
     ladder naming the kept piece that closed the gap, and a second rail on
     an empty bag. Anton read it and said what the 4 September fix had
     already said once — the gap was being stated in a third and fourth
     place on one screen — and he was right. The receipt names the gap, its
     button acts on it, and `taton-cart-recommendations` already ranks its
     cards by it in six levels: goes with the bag and closes the gap, goes
     with the bag, closes the gap, the rest, with `bestseller` breaking ties
     inside a level and never promoting on its own.

     So a kept piece is not a new block with new words. It joins that rail
     and moves to the front of it, and the only mark it carries is the heart
     she lit herself. Nothing here writes copy.

     It also does something the shelf cannot. `Gift ideas` is fifteen pieces
     between $42 and $74, so on an empty bag — a $100 gap — no card in the
     pool closes it and the ranking collapses to "cheapest first". Her list
     is drawn from the whole catalogue, silver median $82, so it is the only
     thing that brings this rail prices the shelf does not carry.
     -------------------------------------------------------------------- */

  /* The gift card text — Anton, 6 October 2026. The field appears in the
     bag's summary only when a line carries the visible «gift for» property.
     On change the text is written into every such line as a property (on
     taton.shop the checkout is Walnut, which forwards neither the note nor
     cart attributes) and into the cart note (which Shopify's own checkout
     keeps). Delegated, because the bag re-renders its summary. */
  let giftNoteBound = false;
  function initGiftNote() {
    if (giftNoteBound) return;
    giftNoteBound = true;
    document.addEventListener('change', (e) => {
      const area = e.target.closest && e.target.closest('[data-taton-gift-note]');
      if (!area) return;
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const cardKey = area.getAttribute('data-card-key');
      const text = area.value.replace(/\s+/g, ' ').trim().slice(0, 300);
      let lines = [];
      try {
        lines = JSON.parse(area.getAttribute('data-lines') || '[]');
      } catch (err) {
        lines = [];
      }
      const post = (path, body) =>
        fetch(root + path, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        }).then((r) => (r.ok ? r.json() : Promise.reject(r)));
      // One line at a time: each change returns the cart, and a changed
      // property gives the line a new key, so the next call uses the old key
      // of a different line, which is still valid.
      let chain = post('cart/update.js', { note: text });
      lines.forEach((l) => {
        // Liquid prints line properties as a list of pairs, cart.js as an object.
        const props = Array.isArray(l.props) ? Object.fromEntries(l.props) : Object.assign({}, l.props);
        if (text) props[cardKey] = text;
        else delete props[cardKey];
        chain = chain.then(() => post('cart/change.js', { id: l.key, quantity: l.qty, properties: props }));
      });
      chain
        .then((cart) => {
          // Fresh keys for the next edit.
          const first = lines[0] && lines[0].props;
          const firstProps = Array.isArray(first) ? Object.fromEntries(first) : first || {};
          const giftKey = Object.keys(firstProps).find((k) => k !== cardKey && k[0] !== '_');
          const fresh = (cart.items || [])
            .filter((it) => it.properties && giftKey && it.properties[giftKey])
            .map((it) => ({ key: it.key, qty: it.quantity, props: it.properties }));
          if (fresh.length) area.setAttribute('data-lines', JSON.stringify(fresh));
          area.classList.add('is-saved');
        })
        .catch(() => area.classList.remove('is-saved'));
    });
  }

  /* Recently viewed — Anton, 5 October 2026: the bag's rail should mix «те,
     що переглядав, те, що клацав щойно» with the shop's bestsellers. A product
     page writes itself to `taton.viewed` (same entry shape as a kept piece,
     newest first, twelve at most); the rail below takes up to two of them
     after her kept pieces. Nothing leaves the browser. */
  const VIEWED_KEY = 'taton.viewed';

  function viewedRead() {
    try {
      const data = JSON.parse(window.localStorage.getItem(VIEWED_KEY) || 'null');
      return data && data.v === 1 && Array.isArray(data.items) ? data.items.filter((i) => i && typeof i.h === 'string') : [];
    } catch (e) {
      return [];
    }
  }

  function initViewed() {
    const pdp = document.querySelector('[data-taton-pdp-handle]');
    if (!pdp) return;
    const entry = savedEntryFromPdp(pdp);
    if (!entry.h || entry.g) return;
    const items = viewedRead().filter((i) => i.h !== entry.h);
    items.unshift({ h: entry.h, p: entry.p, a: entry.a, t: Date.now() });
    try {
      window.localStorage.setItem(VIEWED_KEY, JSON.stringify({ v: 1, items: items.slice(0, 12) }));
    } catch (e) {
      /* storage refused — the rail simply has no viewed pieces */
    }
  }

  function initSavedCart() {
    if (savedCfg().promote === false) return;
    const kept = savedRead();
    const keptHandles = new Set(kept.map((i) => i.h));
    // Kept first; the viewed ones are marked so they can be capped separately.
    const items = kept.concat(viewedRead().filter((i) => !keptHandles.has(i.h)).map((i) => Object.assign({ viewed: 1 }, i)));

    document.querySelectorAll('[data-taton-rail]:not([data-taton-saved-bound])').forEach((rail) => {
      // Only the bag's own recommendation rail. The gift shelf, the home
      // rails and the Kept page's own rail are other propositions.
      const recs = rail.closest('.taton-recs');
      if (!recs) return;
      rail.setAttribute('data-taton-saved-bound', '');
      if (!items.length) return;

      const scope = recs.closest('[data-taton-drawer], [data-taton-cart]') || document;
      let state = null;
      try {
        const el = scope.querySelector('[data-taton-cart-state]');
        state = el ? JSON.parse(el.textContent) : null;
      } catch (e) {
        state = null;
      }
      const gap = (state && Number(state.nextGap)) || 0;

      // What the bag already holds, and what the rail already shows.
      const inBag = new Set();
      scope.querySelectorAll('.taton-line__name[href]').forEach((a) => {
        // The href is percent-encoded on a Cyrillic handle, and `[a-z0-9-]`
        // stopped at the first `%` — `s925-` was what went into the set, so
        // the rail kept offering pieces the bag already held. Take the whole
        // segment and decode it.
        const m = /\/products\/([^/?#]+)/.exec(a.getAttribute('href') || '');
        if (m) {
          try {
            inBag.add(decodeURIComponent(m[1]));
          } catch (e) {
            inBag.add(m[1]);
          }
        }
      });
      const onRail = new Map();
      rail.querySelectorAll('[data-taton-card-handle]').forEach((card) => {
        onRail.set(card.getAttribute('data-taton-card-handle'), card.closest('.taton-recs__item') || card);
      });

      /* Her order inside the rail follows the rail's own rule rather than a
         second one: a piece that closes the gap on its own comes first,
         cheapest of those first; then the rest, newest saved first, which is
         the order they are in already. Four at most — the rail stays the
         shop's rail with her pieces at the head of it, not a list of hers. */
      const eligible = items.filter((i) => !i.viewed && !i.g && i.a !== 0 && !inBag.has(i.h));
      const closes = eligible.filter((i) => gap > 0 && i.p >= gap).sort((a, b) => a.p - b.p);
      const rest = eligible.filter((i) => closes.indexOf(i) === -1);
      // Then up to two pieces she looked at most recently (5 Oct 2026).
      const viewed = items.filter((i) => i.viewed && i.a !== 0 && !inBag.has(i.h)).slice(0, 2);
      const wanted = closes.concat(rest).slice(0, 4).concat(viewed);
      if (!wanted.length) return;

      /* Anything already on the rail only moves; anything missing is fetched,
         and the shop is asked before it is shown — a piece saved a week ago
         may be gone or dearer. Insert in reverse so the first of `wanted`
         ends up leftmost whichever order the answers arrive in. */
      Promise.all(
        wanted.map((i) => (onRail.has(i.h) ? Promise.resolve(null) : savedFetchCard(i.h)))
      ).then((fetched) => {
        if (!rail.isConnected) return;
        let added = false;

        for (let k = wanted.length - 1; k >= 0; k -= 1) {
          const handle = wanted[k].h;
          const existing = onRail.get(handle);
          if (existing) {
            rail.insertBefore(existing, rail.firstChild);
            added = true;
            continue;
          }
          const res = fetched[k];
          if (!res || !res.available || res.g) continue;
          const item = document.createElement('div');
          item.className = 'taton-recs__item';
          item.appendChild(res.card);
          rail.insertBefore(item, rail.firstChild);
          added = true;
        }

        if (!added) return;
        initCards();
        initRails();
        savedSyncHearts();
        /* **Back to the left edge, or the work is invisible.** Prepending to
           a horizontally scrolled container moves the existing cards right,
           and the browser holds them still by adding the same distance to
           `scrollLeft` — scroll anchoring doing exactly its job. Measured on
           7 September: the DOM order was hers-first and the rail still opened
           on the fourth card, because it had silently scrolled past them. */
        rail.scrollLeft = 0;
        // The rail was measured before the cards arrived, so the arrows and
        // the scroll extent are stale until it is measured again.
        rail.dispatchEvent(new Event('scroll'));
      });
    });
  }

  /* ------------------------------------------------------------------
     15. Перевірка залишку перед оплатою — 29 вересня 2026, #9863

     Walnut забирає кошик і створює замовлення вже після оплати, не звіряючи
     залишок, — так 26.09 продали сережки, яких на складі вже не було.
     Кнопку він перехоплює слухачем кліку на `document` у фазі перехоплення
     (w-checkout.js, walnut-checkout-taton-13). Ця перевірка слухає на
     `window`: у фазі перехоплення подія доходить до `window` раніше, ніж до
     `document`, хоч би в якому порядку завантажились скрипти.

     Клік зупиняється, і `sections/taton-cart-stock.liquid` каже, скільки
     кожного варіанта можна продати зараз. Якщо все є, та сама кнопка
     натискається ще раз, уже з пропуском, і Walnut отримує її як завжди.
     Якщо чогось бракує, рядок прибирається або кількість зменшується, кошик
     перемальовується з поясненням, і до Walnut людина не йде: вона бачить
     новий кошик і натискає ще раз сама.

     Якщо не вдалась сама перевірка (мережа, немає секції, довше 5 секунд),
     клік пропускається: заглушка не має права зупиняти продаж через власну
     поломку, а без неї все одно було так само.

     Гонку під час самої оплати це не закриває: між перевіркою й платежем
     минають хвилини, і річ можуть купити саме тоді. Закрити її може лише
     Walnut.
     ------------------------------------------------------------------ */

  // Те, що Walnut вважає кнопкою оформлення, крім «Купити зараз» — той шлях
  // іде через cart/add.js, а він розпроданого не додає.
  const CHECKOUT_TRIGGER =
    '[name="checkout"], a[href*="/checkout"], #checkout, [data-checkout], .cart__checkout-button, .cart__checkout, #CartDrawer-Checkout';
  const STOCK_TIMEOUT = 5000;

  let stockGuardBound = false;
  let stockPass = false;
  let stockBusy = false;
  // { notes: [{ title, keep }], fixed, empty, reveal } — живе до кінця
  // сторінки, бо кошик після виправлення ще кілька разів перемальовується.
  let stockNotice = null;

  function stockCheck() {
    const request = fetch(cartRoot() + '?section_id=taton-cart-stock&_=' + Date.now(), { cache: 'no-store' })
      .then((r) => (r.ok ? r.text() : Promise.reject(new Error('stock ' + r.status))))
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const el = doc.querySelector('[data-taton-cart-stock]');
        if (!el) throw new Error('stock: no data');
        return JSON.parse(el.textContent);
      });
    const timeout = new Promise((resolve, reject) => {
      setTimeout(() => reject(new Error('stock: timeout')), STOCK_TIMEOUT);
    });
    return Promise.race([request, timeout]);
  }

  /* Один варіант може стояти в кількох рядках, а залишок на всі один:
     ділимо його по черзі, решту зменшуємо або прибираємо. */
  function stockShortfall(lines) {
    const byVariant = new Map();
    lines.forEach((l) => {
      if (l.auto || typeof l.max !== 'number') return;
      if (!byVariant.has(l.id)) byVariant.set(l.id, { max: Math.max(0, l.max), lines: [] });
      byVariant.get(l.id).lines.push(l);
    });

    const updates = {};
    const notes = [];
    byVariant.forEach((v) => {
      let left = v.max;
      let short = false;
      v.lines.forEach((l) => {
        const keep = Math.min(l.q, left);
        left -= keep;
        if (keep < l.q) {
          updates[l.key] = keep;
          short = true;
        }
      });
      if (short) notes.push({ title: v.lines[0].t, keep: v.max });
    });
    return { updates, notes };
  }

  function stockProceed(btn, surface) {
    let target = btn;
    // Поки йшла перевірка, кошик міг перемалюватись — тоді шукаємо нову
    // кнопку там само: від'єднаний елемент кліку до Walnut не донесе.
    if (!target.isConnected) {
      const scope = surface ? document.querySelector('[data-taton-' + surface + ']') : null;
      target = scope && scope.querySelector(CHECKOUT_TRIGGER);
    }
    if (!target) return;
    stockPass = true;
    try {
      target.click();
    } finally {
      stockPass = false;
    }
  }

  function stockApply(short, surface) {
    stockNotice = { notes: short.notes, fixed: false, empty: false, reveal: surface };
    return fetch(cartRoot() + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates: short.updates }),
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then((cart) => {
        stockNotice.fixed = true;
        stockNotice.empty = cart.item_count === 0;
        document.querySelectorAll('[data-taton-cart-count]').forEach((el) => {
          el.textContent = cart.item_count;
        });
      })
      .catch(() => {})
      .then(() => Promise.all([refreshDrawer(), refreshCartPage ? refreshCartPage() : null]));
  }

  /* Кличуть initCart і bindDrawer після кожного малювання: пояснення має
     пережити всі перемальовування, що йдуть за виправленням кошика. */
  function renderStockNotice(scope, surface) {
    if (!stockNotice || !scope) return;
    scope.querySelectorAll('[data-taton-stock-notice]').forEach((n) => n.remove());

    // Функції замість рядків заміни: «$&» у назві товару не стане шаблоном.
    const fill = (text, n) => text.replace('[piece]', () => n.title).replace('[count]', () => String(n.keep));
    const box = document.createElement('div');
    box.className = 'taton-sum__stock';
    box.setAttribute('data-taton-stock-notice', '');
    box.setAttribute('role', 'alert');
    box.setAttribute('tabindex', '-1');

    const intro = document.createElement('p');
    intro.textContent = TI.stock_intro || 'While your bag waited, something sold out:';
    box.appendChild(intro);

    const list = document.createElement('ul');
    stockNotice.notes.forEach((n) => {
      const item = document.createElement('li');
      item.textContent =
        n.keep > 0
          ? fill(TI.stock_reduced || '[piece] — only [count] left', n)
          : fill(TI.stock_removed || '[piece] — no longer available', n);
      list.appendChild(item);
    });
    box.appendChild(list);

    const outro = !stockNotice.fixed
      ? TI.stock_failed || "We couldn't update your bag. Please change it yourself and try again."
      : stockNotice.empty
        ? ''
        : TI.stock_again || "We've updated your bag. Have a look and press Checkout again.";
    if (outro) {
      const end = document.createElement('p');
      end.textContent = outro;
      box.appendChild(end);
    }

    const form = scope.querySelector('[data-taton-summary] form');
    if (form) {
      form.parentNode.insertBefore(box, form);
    } else {
      // Порожній кошик: підсумку немає, тож пояснення стає на самий верх.
      box.classList.add('taton-sum__stock--top');
      const head = scope.querySelector('.taton-cart__head');
      const body = scope.querySelector('.taton-drawer__body');
      if (head) head.after(box);
      else if (body) body.prepend(box);
      else scope.prepend(box);
    }

    if (stockNotice.reveal === surface) {
      stockNotice.reveal = '';
      box.scrollIntoView({ block: 'nearest', behavior: reduceMotion.matches ? 'auto' : 'smooth' });
      box.focus({ preventScroll: true });
    }
  }

  function onCheckoutClick(e) {
    if (stockPass) return;
    const t = e.target;
    if (!t || typeof t.closest !== 'function') return;
    const btn = t.closest(CHECKOUT_TRIGGER);
    if (!btn) return;

    e.preventDefault();
    e.stopImmediatePropagation();
    if (stockBusy) return;
    stockBusy = true;

    const surface = btn.closest('[data-taton-drawer]') ? 'drawer' : btn.closest('[data-taton-cart]') ? 'cart' : '';
    btn.setAttribute('aria-busy', 'true');

    stockCheck()
      .then(
        (lines) => {
          const short = stockShortfall(lines);
          if (!short.notes.length) return stockProceed(btn, surface);
          return stockApply(short, surface);
        },
        () => stockProceed(btn, surface)
      )
      .catch(() => {})
      .finally(() => {
        stockBusy = false;
        btn.removeAttribute('aria-busy');
      });
  }

  function initStockGuard() {
    if (stockGuardBound) return;
    stockGuardBound = true;
    window.addEventListener('click', onCheckoutClick, true);
  }

  function boot() {
    initReveal();
    initFooterCols();
    initHeader();
    initMenu();
    initSearch();
    initTheme();
    initAnnouncements();
    initCards();
    initRails();
    initPieceDrift();
    initSort();
    initSubnav();
    initLoadMore();
    initPdp();
    initGiftCard();
    initCart();
    initDrawer();
    initStockGuard();
    initSavedPage();
    initViewed();
    initGiftNote();
    initSavedCart();
    savedSyncHearts();
    savedSyncCounts();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Sections re-render on their own in the theme editor.
  document.addEventListener('shopify:section:load', boot);
})();
